// The candidate pipeline: one table that changes shape with the round.
//
// WHY A TABLE AND NOT CARDS
// The previous version gave every candidate a generous two-line card. It read
// beautifully and it could not answer the question people actually have, which
// is comparative: who here has under 30 days' notice, who is asking for more
// than we pay, who has not been screened. Comparison needs a column — the eye
// runs down one and finds the outlier without reading a word. Six cards of
// prose cannot do that no matter how well they are set.
//
// So: aligned columns, one line per person, and the prose moved into a row you
// expand when a specific candidate has earned the read.
//
// THE THREE EDITABLE COLUMNS ARE THE POINT
// Notice period, salary expectation and real experience decide who is worth
// interviewing at least as often as anything on the CV, and none of them are
// reliably on a CV. They are established on a phone call. Before this they had
// nowhere to go but the recruiter's memory, so the tool knew less about a
// candidate than the person using it did. Click a cell and type.
//
// They live on the candidate record, not in the screening result, because the
// screening result is the model's output and is thrown away and rewritten every
// time a resume is re-read. What a human established has to survive that.
//
// EMPTY IS NOT ZERO, ANYWHERE IN HERE
// A blank notice period means nobody has asked. Zero means they can start on
// Monday, which is a real answer and a good one. Same for salary, same for the
// fit score: "Unclear from CV" is not a 0/10, and a candidate who has never
// interviewed does not have an interview score of nothing. Every column below
// renders the three states — value, zero, unknown — as three different things.
//
// COLOUR IS A SIGNAL, NOT DECORATION
// One accent (indigo) means "the system inferred this". Fit bands are a coloured
// dot against plain text rather than filled pills; with four filled colours on
// every row the colour stops meaning anything. Green and amber appear only
// inside an opened detail panel, where pros and cons genuinely oppose.
//
// STAGE DECIDES THE VERBS
// A candidate who just applied and one mid-interview need different actions, so
// the single primary action is derived from the round:
//
//   SCREENING    -> invite to AI screening (moves them, issues and emails the
//                   link; the server does not separate the two)
//   AI_INTERVIEW -> send or resend the link, or review the result
//   REJECTED     -> put them back, and nothing else
//   anything else-> no primary action; use the bulk move
//
// ON ORDERING
// New applicants sort by the resume's 1-10 fit score, highest first. That score
// rates the DOCUMENT and is the weakest signal here — a strong candidate with a
// thin CV lands low — so the row keeps the band beside it, the detail panel
// carries the quote behind every point, and nothing is filtered by it. It orders
// a reading queue; it does not decide anything.
//
// Once someone has interviewed, the ordering switches to the interview score,
// which came from recorded answers marked against anchors a human wrote.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHref, useNavigate, useSearchParams } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import {
  Box,
  Typography,
  Stack,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Alert,
  Collapse,
  IconButton,
  Tooltip,
  TextField,
  MenuItem,
  InputAdornment,
  LinearProgress,
  Snackbar,
  Divider,
  Dialog,
  DialogContent,
  Popover,
} from "@mui/material";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import QuizOutlinedIcon from "@mui/icons-material/QuizOutlined";
import SearchIcon from "@mui/icons-material/Search";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import RemoveIcon from "@mui/icons-material/Remove";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import UndoRoundedIcon from "@mui/icons-material/UndoRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import StickyNote2OutlinedIcon from "@mui/icons-material/StickyNote2Outlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ViewListRoundedIcon from "@mui/icons-material/ViewListRounded";
import ViewAgendaOutlinedIcon from "@mui/icons-material/ViewAgendaOutlined";
import interviewService from "../services/interviewService";
import { buildInviteMessage } from "../utils/inviteMessage";
import {
  buildPrescreenMessage,
  getChatOpener,
  openPrescreenChat,
  setChatOpener,
} from "../utils/prescreenMessage";
import WhatsAppTemplateDialog from "./WhatsAppTemplateDialog";
import WhatsAppStatusPopover, { WA_STATUS } from "./WhatsAppStatusPopover";
import { whatsappService, enotifyPhone } from "../services/whatsappService";
import WhatsAppInviteDialog from "./WhatsAppInviteDialog";
import RejectReasonDialog from "./RejectReasonDialog";
import { applicationService } from "../services/applicationService";
import { exportCandidatesWorkbook } from "../utils/exportCandidates";

const FONT = "'Plus Jakarta Sans', sans-serif";

// One neutral ramp and one accent. Everything on screen comes from here, which
// is the only reliable way to stop a UI accumulating nine slightly different
// greys and four blues that nearly match.
const C = {
  ink: "#18181b",
  body: "#3f3f46",
  muted: "#71717a",
  faint: "#a1a1aa",
  hair: "#f4f4f5",
  line: "#e4e4e7",
  wash: "#fafafa",
  card: "#ffffff",
  accent: "#6366f1",
  accentSoft: "#eef2ff",
};

// Fit bands read as a dot plus a word. No fill, because a row can carry a band
// AND a verdict AND a warning, and three filled pills is noise.
const BAND = {
  STRONG: { label: "Strong fit", dot: "#10b981" },
  POSSIBLE: { label: "Possible fit", dot: "#3b82f6" },
  UNCLEAR: { label: "Unclear from CV", dot: "#d4d4d8" },
};

const BAND_ORDER = { STRONG: 0, POSSIBLE: 1, UNCLEAR: 2 };

const VERDICT = {
  STRONG_HIRE: { label: "Strong hire", fg: "#047857" },
  HIRE: { label: "Hire", fg: "#0369a1" },
  BORDERLINE: { label: "Borderline", fg: "#b45309" },
  NO_HIRE: { label: "No hire", fg: "#b91c1c" },
  // The AI's suggestion, not a decision: a hard gate was not met (notice,
  // salary, relocation) or the interview was not finished. "Rejected" read as
  // though somebody had already turned the candidate down. The reason is on
  // hover — see VerdictLabel.
  REJECTED: { label: "Suggest reject", fg: "#c2410c" },
};

// The verdict under an interview score. A "Suggest reject" carries an ⓘ;
// clicking it shows why, and lets HR dismiss the suggestion with a reason when
// the gate does not matter for this person. The verdict then falls back to
// what the score alone says — see dismissGate on the server.
const VerdictLabel = ({ row, verdict, sx, onDismiss }) => {
  const [anchor, setAnchor] = useState(null);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const label = <Meta sx={sx}>{verdict?.label || "interview"}</Meta>;
  if (row.interview_verdict !== "REJECTED") return label;

  const close = () => {
    if (saving) return;
    setAnchor(null);
    setReason("");
  };
  const dismiss = async () => {
    setSaving(true);
    try {
      await onDismiss(row, reason.trim());
      setAnchor(null);
      setReason("");
    } catch {
      // The caller has said what went wrong; keep the box open with the text.
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.4}
        onClick={(e) => {
          e.stopPropagation();
          setAnchor(e.currentTarget);
        }}
        sx={{ minWidth: 0, cursor: "pointer", "&:hover": { opacity: 0.8 } }}
      >
        {label}
        <InfoOutlinedIcon sx={{ fontSize: 12, color: verdict?.fg, flexShrink: 0 }} />
      </Stack>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        onClick={(e) => e.stopPropagation()}
        slotProps={{ paper: { sx: { p: 2, width: 320, borderRadius: "10px" } } }}
      >
        <Typography sx={{ fontFamily: FONT, fontSize: 13, fontWeight: 700, color: C.ink }}>
          AI suggestion only
        </Typography>
        <Typography sx={{ fontFamily: FONT, fontSize: 12, color: C.muted, mt: 0.25 }}>
          Nobody has rejected this candidate.
        </Typography>
        <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: C.body, mt: 1.25, lineHeight: 1.5 }}>
          <Box component="span" sx={{ fontWeight: 650 }}>Reason: </Box>
          {row.interview_verdict_reason || "Open Review for the reason."}
        </Typography>
        {onDismiss && (
          <>
            <TextField
              autoFocus
              fullWidth
              multiline
              minRows={2}
              size="small"
              placeholder="Why doesn't this apply? e.g. 45 days' notice is fine for this hire"
              value={reason}
              disabled={saving}
              onChange={(e) => setReason(e.target.value)}
              inputProps={{ maxLength: 500, style: { fontFamily: FONT, fontSize: 12.5 } }}
              sx={{ mt: 1.5 }}
            />
            <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 1.25 }}>
              <Button
                size="small"
                onClick={close}
                disabled={saving}
                sx={{ fontFamily: FONT, textTransform: "none", color: C.muted }}
              >
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                disableElevation
                onClick={dismiss}
                disabled={saving || !reason.trim()}
                sx={{ fontFamily: FONT, textTransform: "none", fontWeight: 650, bgcolor: C.accent }}
              >
                {saving ? "Dismissing…" : "Dismiss suggestion"}
              </Button>
            </Stack>
            <Typography sx={{ fontFamily: FONT, fontSize: 11, color: C.faint, mt: 1 }}>
              The verdict goes back to what the score says. Your reason is saved in the
              candidate's history.
            </Typography>
          </>
        )}
      </Popover>
    </>
  );
};

const MET = {
  YES: { icon: <CheckIcon sx={{ fontSize: 12 }} />, color: "#059669" },
  NO: { icon: <CloseIcon sx={{ fontSize: 12 }} />, color: "#dc2626" },
  UNCLEAR: { icon: <RemoveIcon sx={{ fontSize: 12 }} />, color: "#a1a1aa" },
};

// Which stored date the range filter applies to. Keys match the server's
// DATE_FIELDS whitelist — it maps them to columns, so a name invented here
// would silently fall back to applied_at and filter the wrong thing.
// Labelled as dates, not as states. "Applied" on its own reads as a status
// filter; "Applied on" can only be the thing the range applies to.
const DATE_FIELDS = [
  { key: "applied", label: "Applied on" },
  { key: "screened", label: "CV screened on" },
  { key: "invited", label: "Link sent on" },
  { key: "stage", label: "Entered round on" },
  { key: "rejected", label: "Rejected on" },
];

// ── Filter bar pieces ───────────────────────────────────────────────────────
//
// One chip style and one compact-select style for the whole filter bar, so the
// three chip groups and the dropdowns beside them cannot drift apart.
function filterChipSx(on) {
  return {
    fontFamily: FONT,
    fontSize: 12,
    fontWeight: on ? 700 : 500,
    height: 28,
    borderRadius: 2,
    cursor: "pointer",
    color: on ? "#fff" : C.muted,
    bgcolor: on ? C.ink : C.card,
    border: `1px solid ${on ? C.ink : C.hair}`,
    "&:hover": { bgcolor: on ? C.ink : C.hair },
  };
}

// Outlined in the accent while it is narrowing the list, so a filter left on
// is visible from across the room. 32px tall, like every control on the bar.
function compactSelectSx(active) {
  return {
    flexShrink: 0,
    height: 32,
    justifyContent: "center",
    borderRadius: 2,
    border: `1px solid ${active ? C.accent : C.line}`,
    bgcolor: active ? C.accentSoft : C.card,
    "& .MuiSelect-select": { py: 0.5, pl: 1.25, pr: "28px !important" },
  };
}

// The small outlined buttons on the filter bar. One definition, so they are
// all the same height and weight — the difference between a bar and a pile.
const toolBtnSx = {
  height: 32,
  px: 1.25,
  minWidth: 0,
  fontFamily: FONT,
  fontSize: 12.5,
  fontWeight: 650,
  textTransform: "none",
  whiteSpace: "nowrap",
  borderRadius: 2,
  border: `1px solid ${C.line}`,
  bgcolor: C.card,
  color: C.ink,
  "& .MuiButton-startIcon": { mr: 0.6 },
  "&:hover": { borderColor: C.accent, bgcolor: C.card },
  "&.Mui-disabled": { color: C.faint, borderColor: C.hair },
};

// ── Column geometry ─────────────────────────────────────────────────────────
//
// Defined once and used by both the header and every row. Two lists that have
// to agree is one list too many: the moment they drift, every heading sits over
// the wrong column and the table is worse than no table.
const COLS = [
  { key: "check", w: "34px" },
  // Name with the email under it, so the column only needs the longer of the
  // two — a typical address fits; a longer one is cut short. Fixed, so spare
  // room goes to the note column (see below) rather than becoming a gap.
  { key: "candidate", w: "230px" },
  // ── Everything from here right used to be sized to just fit its content ──
  //
  // Which made the whole right-hand half of the row read as one dense block:
  // a phone number, an experience, a notice period and a salary with barely a
  // character between them. These are all short values that get COMPARED down
  // the column, so each one needs enough room around it to be picked out
  // without the eye having to find the boundary first.
  { key: "phone", w: "130px" },
  // Experience and notice hold a few characters each ("4.6", "11d"), so they
  // were narrowed to make room for the note column beside them.
  { key: "experience", w: "56px" },
  { key: "notice", w: "60px" },
  { key: "salary", w: "116px" },
  // The note used to sit under the name inside the candidate column. The one
  // stretchy column: on a wide screen the extra room becomes a longer note
  // box rather than empty space somewhere in the row.
  { key: "note", w: "minmax(160px, 1fr)" },
  // Sized to the longest values — "8/10 Strong hire", "Sent, not started",
  // "24 Oct · Screened". Wider just left a gap between score, dates and CV.
  { key: "score", w: "96px" },
  { key: "dates", w: "70px" },
  // Its own column now, out of the action cluster. Opening a CV is the single
  // most common thing done on one of these rows, and it was the fourth of six
  // identically-sized grey icons.
  { key: "resume", w: "40px" },
  { key: "actions", w: "324px" },
];
const GRID = COLS.map((c) => c.w).join(" ");
// 10px between columns. At 8 the values in adjacent cells read as one run-on
// string — an experience of 5.2 a hair from a notice period of 30 is two numbers
// you have to separate by eye before you can use either. 14 left the right half
// of the row (score, dates, CV, actions) too spread out.
const GAP = 10;
const PAD = 12;
// Inside the actions cell: four icon slots, a button and a chevron.
// Named so the cell's width above and the slots below cannot drift apart —
// 4*28 + 190 + 22 = 324, which is what the 324px column is for.
//
// BTN MUST FIT THE LONGEST PRIMARY LABEL, which is "Invite to AI Interview" on
// the resume screening round. This is not a cosmetic budget. The button box is
// fixed-width and right-aligned, so a label wider than BTN does not wrap or
// clip — it overflows LEFTWARDS, straight over the reject and delete icons
// sitting beside it, and they stop being clickable.
//
// That is exactly what happened: BTN was 90, sized for a label reading "AI
// round". The label was later widened to say where the candidate actually goes
// — a good change — with a note claiming the column was wide enough, but
// neither this number nor the column above moved. The result was a screening
// round where candidates could not be rejected or deleted at all, on rows whose
// buttons looked perfectly normal. If you change a primary label, change this.
const ICON = 28;
const BTN = 190;

// The row's true minimum: the fixed columns, the candidate column at its 170px
// floor, the gaps and the padding. It is a safety net, not a target — the
// widths above are chosen so the table fits a laptop beside the collapsed nav
// rail with as little scrolling as possible.
//
// Currently 1440px: 1320px, then 1418px when the actions column grew to stop
// the primary button covering reject and delete, then 1542px when the note got
// its own column, then 1440px when score, dates, CV and the column gap were
// tightened. Below ~1515px of viewport the
// table scrolls horizontally instead of clipping, which is what TABLE_MIN is
// for — but it is worth knowing the number moved, and why, before widening
// any other column.
//
// It has to be the ACTUAL sum. Set lower, the grid overflows this box rather
// than the scroll container and the last column is clipped instead of reachable.
const TABLE_MIN =
  COLS.reduce((n, c) => n + (parseInt(c.w.replace(/^minmax\(/, ""), 10) || 0), 0) +
  (COLS.length - 1) * GAP +
  PAD * 2;

// ── Formatting ──────────────────────────────────────────────────────────────

// "12 Aug" this year, "12 Aug 24" otherwise. The year is noise on the ninety
// per cent of rows that are from this year, and essential on the ten that
// are not.
function fmtDate(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    ...(sameYear ? {} : { year: "2-digit" }),
  });
}

function fmtDateTime(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function daysSince(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return Math.max(0, Math.floor((Date.now() - d.getTime()) / 86400000));
}

// Indian grouping, because that is the market these salaries are quoted in and
// 1,200,000 reads as a different number to 12,00,000 to the person checking it.
// Compacted to lakhs above a lakh: the full figure is on the tooltip, and a
// column of eight-digit numbers is unreadable at a glance.
function fmtSalary(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  if (n >= 10000000) return `${(n / 10000000).toFixed(n % 10000000 ? 2 : 0)} Cr`;
  if (n >= 100000) return `${(n / 100000).toFixed(n % 100000 ? 1 : 0)} L`;
  return n.toLocaleString("en-IN");
}

function fmtSalaryFull(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return `₹${n.toLocaleString("en-IN")} per year`;
}

// Digits only, with an Indian country code when the number looks like a bare
// ten-digit mobile. wa.me rejects anything else, and a link that opens WhatsApp
// to "number not on WhatsApp" is worse than a disabled button.
function waNumber(phone) {
  const digits = String(phone || "").replace(/\D/g, "");
  if (!digits) return null;
  if (digits.length === 10) return `91${digits}`;
  if (digits.length === 11 && digits.startsWith("0")) return `91${digits.slice(1)}`;
  if (digits.length >= 11 && digits.length <= 15) return digits;
  return null;
}

function interviewState(row) {
  if (!row.invited) return { key: "NONE", label: "Link not sent", dot: C.faint };
  switch (row.interview_status) {
    case "SCHEDULED":
      return { key: "SENT", label: "Sent, not started", dot: "#0891b2" };
    case "IN_PROGRESS":
      return { key: "LIVE", label: "In progress", dot: "#d97706" };
    case "SUBMITTED":
      return { key: "SCORING", label: "Scoring", dot: "#7c3aed" };
    case "SCORED":
      return { key: "DONE", label: "Interviewed", dot: "#059669" };
    case "EXPIRED":
      return { key: "EXPIRED", label: "Link expired", dot: "#b91c1c" };
    default:
      return { key: "SENT", label: row.interview_status || "Invited", dot: C.muted };
  }
}

// ── Where you were, kept in the address bar ────────────────────────────────
//
// Opening a candidate used to replace this page, and every bit of "where was
// I" lived in component state — so Back landed on the first round, page 1,
// scrolled to the top, with nothing saying which candidate you had just read.
// Held in the URL instead, the same view survives Back, a reload, a remount
// after an upload, and a link pasted to a colleague.
//
// Short keys, defaults left out, so a fresh job still has a clean URL. Only
// keys this component owns are touched; anything else on the URL is left alone.
const VIEW_STORAGE_KEY = "ats.pipeline.view";
const URL_KEYS = ["round", "q", "cv", "sat", "wa", "sort", "pg", "per", "df", "from", "to", "open"];
const PER_PAGE_OPTIONS = [10, 25, 50, 100];

// ── The WhatsApp pre-screen, as filters ─────────────────────────────────────
//
// What separates the candidates you have worked from the ones you have not.
// Without it, ten new uploads sort in among the five you already messaged by
// resume score, and the list stops saying who is next. "To contact" is the
// to-do list: new uploads land there, and people leave it as you work them.
//
// SKIPPED counts as Interested: both mean "may be invited now".
// The search box: name, email or phone. `q` is already trimmed and lowercased.
// One definition for the round being worked and for "found in other rounds",
// so the two can never disagree about who matches.
const matchesSearch = (r, q) =>
  r.candidate_name?.toLowerCase().includes(q) ||
  r.email?.toLowerCase().includes(q) ||
  String(r.phone || "").includes(q);

const WA_FILTERS = [
  { key: "ALL", label: "All", match: () => true },
  { key: "TO_CONTACT", label: "To contact", match: (s) => !s },
  { key: "MESSAGED", label: "Messaged", match: (s) => s === "MESSAGED" },
  { key: "INTERESTED", label: "Interested", match: (s) => s === "INTERESTED" || s === "SKIPPED" },
  { key: "NO_REPLY", label: "No reply", match: (s) => s === "NO_REPLY" },
  { key: "NOT_INTERESTED", label: "Not interested", match: (s) => s === "NOT_INTERESTED" },
];

// Statuses that clear a candidate for the AI interview on a pre-screen job.
const WA_CLEARED = new Set(["INTERESTED", "SKIPPED"]);

// "The latest upload": everyone who arrived within this window of the newest
// arrival. A bulk upload takes seconds to a few minutes; this is generous
// enough to hold one together and far shorter than the gap between two.
const BATCH_WINDOW_MS = 15 * 60 * 1000;
// And only while it is recent. A "New" badge on last month's upload is noise.
const NEW_FOR_MS = 7 * 24 * 60 * 60 * 1000;

function readPipelineParams(params) {
  const per = Number(params.get("per"));
  const pg = Number(params.get("pg"));
  const df = params.get("df");
  const date = (k) => (/^\d{4}-\d{2}-\d{2}$/.test(params.get(k) || "") ? params.get(k) : "");
  return {
    tab: params.get("round") || null,
    search: params.get("q") || "",
    screenFilter: params.get("cv") || "ALL",
    sat: params.get("sat") || "ALL",
    // null = "the default for this job", decided once the job is loaded.
    waFilter: WA_FILTERS.some((f) => f.key === params.get("wa")) ? params.get("wa") : null,
    sortBy: params.get("sort") === "newest" ? "newest" : "score",
    page: Number.isInteger(pg) && pg > 1 ? pg : 1,
    perPage: PER_PAGE_OPTIONS.includes(per) ? per : 10,
    dateField: DATE_FIELDS.some((f) => f.key === df) ? df : "applied",
    from: date("from"),
    to: date("to"),
    open: Number(params.get("open")) || null,
  };
}

// The phone app has no second tab to open — a new window there leaves the app
// for the system browser, where nobody is signed in. It keeps the old
// same-screen navigation, and Back restores the view from the URL.
const IS_NATIVE = Capacitor.isNativePlatform();

// Concurrency-limited map. Used for bulk invites, where each item is a model
// call, a credit charge and an email.
//
// Serial would take a minute for twelve candidates; unbounded would open twelve
// simultaneous invites, and the server does a credit check per invite that is
// only correct if they are not all racing each other past it. Three at a time
// is fast enough to watch and slow enough to be honest.
async function mapWithLimit(items, limit, fn) {
  const results = new Array(items.length);
  let cursor = 0;
  const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
    for (;;) {
      const i = cursor++;
      if (i >= items.length) return;
      results[i] = await fn(items[i], i);
    }
  });
  await Promise.all(workers);
  return results;
}

// ── Small presentational pieces ─────────────────────────────────────────────

const Dot = ({ color, pulse }) => (
  <Box
    sx={{
      width: 6,
      height: 6,
      borderRadius: "50%",
      bgcolor: color,
      flexShrink: 0,
      animation: pulse ? "pipeBreathe 1.6s ease-in-out infinite" : "none",
    }}
  />
);

const Meta = ({ children, sx, title }) => (
  <Typography
    title={title}
    sx={{ fontFamily: FONT, fontSize: 12, color: C.muted, ...sx }}
    noWrap
  >
    {children}
  </Typography>
);

const Evidence = ({ text }) =>
  text ? (
    <Typography
      sx={{
        fontFamily: FONT,
        fontSize: 12.5,
        lineHeight: 1.65,
        color: C.faint,
        borderLeft: `2px solid ${C.line}`,
        pl: 1.5,
        mt: 0.5,
      }}
    >
      {text}
    </Typography>
  ) : null;

// A cell you click and type into.
//
// WHY NOT A PERMANENT INPUT IN EVERY CELL
// Three inputs per row across forty rows is a hundred and twenty boxes, and a
// table of boxes reads as a form to fill in rather than a list to scan. These
// look like text until you click them.
//
// WHY IT SAVES ON BLUR AS WELL AS ON ENTER
// The realistic sequence is: type a notice period, click the next cell, type
// that. Requiring Enter means the first edit is silently lost by the click that
// starts the second, which is the worst possible failure for an input somebody
// is using while on the phone. Escape is the way out without saving.
const EditableCell = ({
  value,
  display,
  title,
  placeholder,
  suffix,
  onSave,
  align = "left",
  numeric = false,
  maxLength,
  // Opened by something else — the pencil beside the candidate's name, whose
  // own click already opens the drawer. `onClose` lets that owner drop back to
  // the read-only view when editing finishes, however it finishes.
  startOpen = false,
  onClose,
}) => {
  const [editing, setEditing] = useState(startOpen);
  const [draft, setDraft] = useState(startOpen && value != null ? String(value) : "");
  const [saving, setSaving] = useState(false);
  // Set by Escape so the blur that immediately follows does not save the very
  // draft Escape was cancelling.
  const cancelled = useRef(false);

  const begin = (e) => {
    e.stopPropagation();
    if (saving) return;
    cancelled.current = false;
    setDraft(value == null ? "" : String(value));
    setEditing(true);
  };

  const commit = async () => {
    if (cancelled.current) {
      cancelled.current = false;
      setEditing(false);
      onClose?.();
      return;
    }
    const next = draft.trim();
    // Compared as strings so that clearing a field ("" vs null) and retyping
    // the same number both come out as "nothing changed" and skip the request.
    const before = value == null ? "" : String(value);
    if (next === before) {
      setEditing(false);
      onClose?.();
      return;
    }
    setSaving(true);
    try {
      // "" means clear. The caller turns it into an explicit null, which the
      // server distinguishes from a field that was not sent at all.
      await onSave(next === "" ? null : next);
      setEditing(false);
      onClose?.();
    } catch {
      // Stay in edit mode with what they typed still there. Dropping back to
      // the old value would look like the save worked and then reverted.
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <TextField
        autoFocus
        size="small"
        // Outlined, not standard. A bare underline in the middle of a table
        // does not read as somewhere you can type — it reads as the cell being
        // underlined for emphasis, which is why people clicked, saw a line
        // appear, and clicked away again.
        variant="outlined"
        value={draft}
        disabled={saving}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === "Enter") {
            e.preventDefault();
            // e.target, not e.currentTarget: TextField puts onKeyDown on its
            // wrapper div, and blurring a div that never had focus does
            // nothing — Enter silently did not save.
            e.target.blur();
          }
          if (e.key === "Escape") {
            cancelled.current = true;
            setEditing(false);
          }
        }}
        inputProps={{
          // inputMode rather than type="number": a number input eats the comma
          // in "12,00,000" and scrolls the value when the wheel passes over it,
          // which silently changes a salary somebody is reading past.
          inputMode: numeric ? "numeric" : "text",
          // Only where the value has a real ceiling — a notice period is at
          // most three digits, and a box that accepts eight is a box somebody
          // eventually pastes a salary into.
          ...(maxLength ? { maxLength } : {}),
          style: {
            fontFamily: FONT,
            fontSize: 12,
            textAlign: align,
            padding: "5px 7px",
          },
        }}
        sx={{
          width: "100%",
          "& .MuiOutlinedInput-root": {
            borderRadius: "6px",
            bgcolor: C.card,
            "& fieldset": { borderColor: C.accent },
          },
        }}
      />
    );
  }

  const empty = value == null || value === "";
  // No hover text. It sat over the row below and covered the very values being
  // compared down the column; the faint box and the text cursor already say
  // "you can type here". The description is kept for screen readers only.
  return (
    <Box
      onClick={begin}
      aria-label={saving ? "Saving…" : title || "Click to edit"}
      sx={{
        cursor: "text",
        minWidth: 0,
        textAlign: align,
        // A permanent, very faint box. These three columns are the only
        // editable things on the row and nothing said so — a hover highlight
        // is invisible to somebody who never happens to hover. Faint enough
        // that thirty of them down the page do not read as a form.
        borderRadius: "6px",
        border: `1px solid ${C.hair}`,
        px: 0.75,
        py: "4px",
        opacity: saving ? 0.5 : 1,
        transition: "background .12s, border-color .12s",
        "&:hover": { bgcolor: C.wash, borderColor: C.line },
      }}
    >
      <Typography
        sx={{
          fontFamily: FONT,
          fontSize: 12,
          // A dash for "nobody has asked" and a real value for everything
          // else, including zero. `display ?? …` rather than `display || …`,
          // because "0" is a legitimate answer and falsy.
          color: empty ? C.faint : C.body,
          fontStyle: empty ? "italic" : "normal",
        }}
        noWrap
      >
        {empty ? placeholder || "—" : display ?? String(value)}
        {!empty && suffix ? (
          <Box component="span" sx={{ color: C.faint, ml: 0.3, fontSize: 10.5 }}>
            {suffix}
          </Box>
        ) : null}
      </Typography>
    </Box>
  );
};

// ── The column headings ─────────────────────────────────────────────────────

const HeadCell = ({ children, align = "left" }) => (
  <Typography
    sx={{
      fontFamily: FONT,
      fontSize: 10,
      fontWeight: 750,
      letterSpacing: 0.6,
      textTransform: "uppercase",
      color: C.faint,
      textAlign: align,
    }}
    noWrap
  >
    {children}
  </Typography>
);

const TableHead = ({ allChecked, someChecked, onToggleAll, selectable }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: GRID,
      alignItems: "center",
      gap: `${GAP}px`,
      px: `${PAD}px`,
      py: 0.85,
      position: "sticky",
      top: 0,
      zIndex: 2,
      bgcolor: C.wash,
      borderRadius: "8px",
      border: `1px solid ${C.hair}`,
      mb: 0.75,
    }}
  >
    <Box sx={{ display: "grid", placeItems: "center" }}>
      {selectable && (
        <Tooltip title={allChecked ? "Clear selection" : "Select everyone shown"}>
          <Checkbox
            size="small"
            checked={allChecked}
            indeterminate={!allChecked && someChecked}
            onChange={onToggleAll}
            sx={{ p: 0.25, color: C.line, "&.Mui-checked": { color: C.accent } }}
          />
        </Tooltip>
      )}
    </Box>
    <HeadCell>Candidate</HeadCell>
    <HeadCell>Phone</HeadCell>
    <HeadCell>Exp</HeadCell>
    <HeadCell>Notice</HeadCell>
    <HeadCell>Expected CTC</HeadCell>
    <HeadCell>Note</HeadCell>
    <HeadCell>Score</HeadCell>
    <HeadCell>Dates</HeadCell>
    <HeadCell align="center">CV</HeadCell>
    <HeadCell align="right">Actions</HeadCell>
  </Box>
);

// ── What a row can do ───────────────────────────────────────────────────────

// What a row's primary button does, by round type — shared by the list row
// and the card, so the two views can never offer different actions. `held`
// is the WhatsApp pre-screen: the invite stays disabled until they agree.
function primaryActionFor(row, mode, held) {
  const state = interviewState(row);
  const rejected = row.status === "REJECTED";
  // Nothing to do to a rejected candidate except put them back, and that is
  // the undo button. Sending an interview link to somebody who has been
  // turned down is the one mistake this state exists to prevent.
  if (rejected) return null;
  if (mode === "SCREENING") {
    return {
      // Says where the candidate goes, not just that they move. It was "AI
      // round", trimmed to save 90px per row — but on a tab now named for the
      // round you are LEAVING, a two-word label gave no clue where the button
      // sends them. The tooltip still carries what else happens.
      //
      // The column was NOT wide enough when this label landed, whatever the
      // note here used to claim: BTN and the actions column were both sized
      // for the old two-word label, and this one overflowed its box onto the
      // reject and delete icons. Widening a label here means widening BTN.
      //
      // "Invite", not "Move". The server has no way to put somebody in the AI
      // round without also inviting them — entering it IS the invite — so a
      // label promising a quiet move promised something nothing could do.
      label: "Invite to AI Interview",
      icon: <ArrowForwardIcon sx={{ fontSize: 15 }} />,
      // Held for the WhatsApp pre-screen: same label, so the button keeps
      // its width (see BTN), disabled until they agree.
      disabled: !row.email || held,
      hint: held
        ? "Waiting for them to agree on WhatsApp. Mark them Interested — or Skip WhatsApp — from the status under their phone number."
        : row.email
          ? "Moves to the AI/Call Interview round, issues the interview link and emails it"
          : "No email address on file — the interview link is delivered by email",
    };
  }
  if (mode === "AI_INTERVIEW") {
    if (state.key === "DONE") return null;
    const first = state.key === "NONE";
    return {
      // "Send link" rather than "Send": on this tab the candidate is already
      // in the round, so the question is what is being sent.
      label: first ? "Send link" : "Resend link",
      icon: <SendOutlinedIcon sx={{ fontSize: 14 }} />,
      disabled: !row.email,
      subtle: !first,
      hint: first
        ? "Emails the interview link to this candidate"
        : "Issues a NEW link and emails it. The link they already have stops working immediately.",
    };
  }
  return null;
}

// ── The action cluster ──────────────────────────────────────────────────────
//
// Copy, WhatsApp, reject, delete and the primary button. One component used by
// BOTH the table row and the card, deliberately: two copies of these buttons is
// how one view ends up able to invite somebody the other would hold back for
// the WhatsApp pre-screen. Same handlers, same guards, same widths.
const RowActions = ({
  row,
  mode,
  busy,
  held,
  prescreen,
  onPrimary,
  onReview,
  onReject,
  onUnreject,
  onDelete,
  onCopyLink,
  onWhatsApp,
  onPrescreenChat,
}) => {
  const working = busy === row.application_id;
  const rejected = row.status === "REJECTED";
  const primary = primaryActionFor(row, mode, held);
  const waTo = waNumber(row.phone);
  const canCopyLink = Boolean(row.interview_url);
  // Every icon sits in a slot of the same fixed width. Laid out inline, a row
  // that happens to have a resume icon pushed its neighbours to a different x
  // than the row above — which is what made the list look broken even though
  // every row was correct.
  const slot = { width: ICON, display: "grid", placeItems: "center", flexShrink: 0 };

  return (
    <>
      {/* Copy the link, and send it by hand. These READ the link; they never
          re-issue it. Re-issuing rotates the token and kills the link the
          candidate is already holding, so the only action that used to put a
          link on screen was the one that broke what you were sending. */}
      <Box sx={slot}>
        {row.invited && (
          <Tooltip
            title={
              canCopyLink
                ? // Says what it copies, because it no longer copies what
                  // the icon implies, and names the escape hatch — a
                  // modifier nobody is told about is a modifier nobody uses.
                  "Copy the whole invitation, ready to paste into WhatsApp, SMS or email. Shift-click for just the link."
                : "This link can no longer be shown — expired, or issued before links could be copied. Resend to get a new one."
            }
          >
            <span>
              <IconButton
                size="small"
                sx={{ p: 0.5 }}
                disabled={!canCopyLink}
                onClick={(e) => onCopyLink(row, e.shiftKey)}
              >
                <ContentCopyIcon sx={{ fontSize: 15, color: C.faint }} />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>

      <Box sx={slot}>
        {/* Before the interview: the pre-screen message, opened in
            WhatsApp for the recruiter to send. Same slot as the invite's
            WhatsApp button, which only appears once a link exists — the
            two never share a row. */}
        {prescreen && !row.invited && (
          <IconButton
            size="small"
            sx={{ p: 0.5 }}
            disabled={!waTo}
            aria-label="Open WhatsApp chat"
            onClick={() => onPrescreenChat(row)}
          >
            <WhatsAppIcon sx={{ fontSize: 16, color: waTo ? "#25d366" : C.faint }} />
          </IconButton>
        )}
        {row.invited && (
          <Tooltip
            title={
              !waTo
                ? "No usable phone number on file"
                : !canCopyLink
                  ? "No link to send. Resend the invitation first."
                  : "Send the invitation on WhatsApp"
            }
          >
            <span>
              <IconButton
                size="small"
                sx={{ p: 0.5 }}
                disabled={!waTo || !canCopyLink}
                onClick={() => onWhatsApp(row)}
              >
                <WhatsAppIcon
                  sx={{ fontSize: 16, color: waTo && canCopyLink ? "#25d366" : C.faint }}
                />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>

      <Box sx={slot}>
        <Tooltip title={rejected ? "Put back in the pipeline" : "Reject"}>
          <span>
            <IconButton
              size="small"
              sx={{ p: 0.5 }}
              disabled={working}
              // Same button, both directions. The undo belongs where the
              // misclick happened, not somewhere else on the screen.
              onClick={() => (rejected ? onUnreject(row) : onReject(row))}
            >
              {rejected ? (
                <UndoRoundedIcon sx={{ fontSize: 16, color: C.accent }} />
              ) : (
                <BlockOutlinedIcon sx={{ fontSize: 16, color: C.faint }} />
              )}
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* Delete sits beside reject because that is where somebody looking to
          get rid of a row will look — but it is a different act, and the
          tooltip says so. Reject records that you said no; this erases that
          they were ever here. */}
      <Box sx={slot}>
        <Tooltip title="Delete permanently">
          <span>
            <IconButton
              size="small"
              sx={{ p: 0.5 }}
              disabled={working}
              onClick={() => onDelete(row)}
            >
              <DeleteOutlineRoundedIcon
                sx={{
                  fontSize: 16,
                  color: C.faint,
                  transition: "color .12s",
                  "&:hover": { color: "#dc2626" },
                }}
              />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* The interview review is an explicit button. It used to open on any
          row click at any stage, which is how someone scanning new
          applicants ended up reading interview questions for a person who
          had not interviewed. */}
      <Box sx={{ width: BTN, display: "flex", justifyContent: "flex-end", flexShrink: 0 }}>
        {row.interview_verdict ? (
          <Button
            size="small"
            variant="text"
            onClick={() => onReview(row)}
            sx={{
              fontFamily: FONT,
              textTransform: "none",
              fontWeight: 650,
              fontSize: 12,
              color: C.accent,
              px: 1,
              minWidth: 0,
              borderRadius: 1.5,
              whiteSpace: "nowrap",
              "&:hover": { bgcolor: C.accentSoft },
            }}
          >
            Review
          </Button>
        ) : primary ? (
          // No hover text — it covered the next row's buttons. `hint` is kept
          // for screen readers.
          <Button
            size="small"
            variant={primary.subtle ? "text" : "contained"}
            disableElevation
            startIcon={primary.icon}
            disabled={primary.disabled || working}
            aria-label={`${primary.label}. ${primary.hint}`}
            onClick={() => onPrimary(row)}
            sx={{
              fontFamily: FONT,
              textTransform: "none",
              fontWeight: 650,
              fontSize: 12,
              borderRadius: 1.75,
              px: 1.25,
              py: 0.45,
              minWidth: 0,
              whiteSpace: "nowrap",
              "& .MuiButton-startIcon": { mr: 0.5 },
              ...(primary.subtle
                ? { color: C.muted, "&:hover": { bgcolor: C.wash } }
                : {
                    bgcolor: C.ink,
                    color: "#fff",
                    "&:hover": { bgcolor: "#27272a" },
                    "&.Mui-disabled": { bgcolor: C.hair, color: C.faint },
                  }),
            }}
          >
            {working ? "…" : primary.label}
          </Button>
        ) : null}
      </Box>
    </>
  );
};

// ── One candidate as a card ─────────────────────────────────────────────────
//
// The other way to read the same list, as a job board shows it: where they
// worked, what they studied, the skills the CV names, and the numbers. The
// file header says why the table is the default — comparing people needs
// columns — and that still holds. Cards are for reading candidates one at a
// time, and on a phone, where the table is 1,400px of sideways scrolling.
//
// Read-only on purpose. Notice, salary and experience are edited in the
// list, where they sit in comparable columns. Everything a card CAN do, it
// does through RowActions and the same handlers as the row, so neither view
// can invite somebody the other would hold back.
const CandidateCard = ({
  row,
  mode,
  index,
  busy,
  checked,
  onCheck,
  onOpen,
  highlight,
  onActivate,
  isNew,
  prescreen,
  held,
  onEditWhatsApp,
  onDismissVerdict,
  ...actionProps
}) => {
  const profileHref = useHref(`/ats/candidate/${row.application_id}`);
  const rejected = row.status === "REJECTED";
  const band = BAND[row.band];
  const verdict = VERDICT[row.interview_verdict];
  const interviewed = row.interview_scored && row.interview_score != null;
  const waState = WA_STATUS[row.whatsapp?.status ?? null] || WA_STATUS.null;
  const waNote = row.whatsapp?.note || null;

  const facts = (kind, n) =>
    (row.highlights || [])
      .filter((h) => h.kind === kind && h.detail)
      .slice(0, n)
      .map((h) => h.detail);
  const employers = [row.current_company, ...facts("EMPLOYER", 3)]
    .filter(Boolean)
    .filter((v, i, all) => all.findIndex((x) => x.toLowerCase() === v.toLowerCase()) === i)
    .slice(0, 2);
  const education = facts("EDUCATION", 2);
  const skills = facts("TOOL", 10);
  const projects = facts("PROJECT", 2);
  const certifications = facts("CERTIFICATION", 3);

  // The job's requirements, scored against the CV. "Meets 4 of 6" plus what is
  // missing — the gap is the part worth asking about on the call.
  const reqs = row.requirements || [];
  const reqsMet = reqs.filter((r) => r.met === "YES").length;
  const reqsMissing = reqs.filter((r) => r.met === "NO").map((r) => r.requirement);
  const pros = (row.strengths || []).slice(0, 2).map((s) => s.point).filter(Boolean);
  const cons = (row.concerns || []).slice(0, 2).map((c) => c.point).filter(Boolean);
  const state = interviewState(row);

  // What a human recorded wins; the CV's claim is shown, marked, when nobody
  // has. A bare number gets its unit — "1.5" on its own reads as anything.
  const experience = row.experience
    ? /^\d+(\.\d+)?$/.test(String(row.experience).trim())
      ? `${row.experience} yrs exp`
      : row.experience
    : row.years_experience != null
      ? `${row.years_experience} yrs exp (CV)`
      : null;
  const notice =
    row.notice_period_days == null
      ? null
      : Number(row.notice_period_days) === 0
        ? "Immediate"
        : `${row.notice_period_days}d notice`;

  const Fact = ({ label, children }) =>
    children ? (
      <Stack direction="row" spacing={1.5} sx={{ minWidth: 0 }}>
        <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: C.faint, width: 78, flexShrink: 0 }}>
          {label}
        </Typography>
        <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: C.body, lineHeight: 1.55, minWidth: 0 }}>
          {children}
        </Typography>
      </Stack>
    ) : null;

  return (
    <Box
      id={`cand-row-${row.application_id}`}
      // Capture phase, so the marker moves whatever inside the card was
      // clicked or tabbed to — and before that control's own handler runs.
      onPointerDownCapture={() => onActivate?.(row.application_id)}
      onFocusCapture={() => onActivate?.(row.application_id)}
      sx={{
        bgcolor: checked ? C.accentSoft : C.card,
        border: `1px solid ${checked ? "#c7d2fe" : C.hair}`,
        boxShadow: highlight ? `inset 3px 0 0 ${C.accent}` : "none",
        borderRadius: "10px",
        p: 1.75,
        opacity: rejected ? 0.72 : 1,
        display: "flex",
        flexDirection: "column",
        gap: 1.25,
        minWidth: 0,
        animation: `pipeIn .3s ease ${Math.min(index, 8) * 0.025}s both`,
        transition: "border-color .16s, box-shadow .16s",
        "&:hover": { opacity: 1, borderColor: checked ? "#a5b4fc" : C.line },
      }}
    >
      {/* Who, and the number that ranks them. */}
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Checkbox
          size="small"
          checked={checked}
          onChange={() => onCheck(row.application_id)}
          sx={{ p: 0.25, mt: "1px", color: C.line, "&.Mui-checked": { color: C.accent } }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
            <Typography
              component={onOpen ? "a" : "p"}
              {...(onOpen
                ? {
                    href: profileHref,
                    target: "_blank",
                    rel: "noopener",
                    onClick: (e) => onOpen(row, e),
                    onAuxClick: (e) => e.button === 1 && onOpen(row, e),
                  }
                : {})}
              title={row.candidate_name}
              noWrap
              sx={{
                display: "block",
                fontFamily: FONT,
                fontWeight: 700,
                fontSize: 15,
                color: rejected ? C.muted : C.ink,
                textDecoration: rejected ? "line-through" : "none",
                cursor: onOpen ? "pointer" : "default",
                "&:hover": onOpen ? { color: C.accent } : undefined,
              }}
            >
              {row.candidate_name}
            </Typography>
            {isNew && (
              <Box
                component="span"
                sx={{
                  flexShrink: 0,
                  px: 0.6,
                  borderRadius: 1,
                  fontFamily: FONT,
                  fontSize: 9.5,
                  fontWeight: 800,
                  letterSpacing: 0.4,
                  lineHeight: "16px",
                  color: C.accent,
                  bgcolor: C.accentSoft,
                }}
              >
                NEW
              </Box>
            )}
            {row.parse_status === "NEEDS_MANUAL_REVIEW" && (
              <Tooltip title={row.parse_error || "The resume could not be read"}>
                <WarningAmberRoundedIcon sx={{ fontSize: 14, color: "#d97706", flexShrink: 0 }} />
              </Tooltip>
            )}
          </Stack>
          <Meta sx={{ mt: 0.35 }}>
            {[
              experience,
              row.expected_salary_annual != null ? `₹${fmtSalary(row.expected_salary_annual)}/yr` : null,
              notice,
              row.city,
              row.applied_at ? `Applied ${fmtDate(row.applied_at)}` : null,
            ]
              .filter(Boolean)
              .join("  ·  ") || "Experience, salary and notice not recorded yet"}
          </Meta>
        </Box>
        <Box sx={{ textAlign: "right", flexShrink: 0 }}>
          {interviewed ? (
            <>
              <Typography sx={{ fontFamily: FONT, fontSize: 15, fontWeight: 750, color: C.ink }}>
                {Math.round(Number(row.interview_score))}
                <Box component="span" sx={{ color: C.faint, fontWeight: 500, fontSize: 11 }}>/100</Box>
              </Typography>
              <VerdictLabel
                row={row}
                verdict={verdict}
                onDismiss={onDismissVerdict}
                sx={{ fontSize: 11, color: verdict?.fg || C.muted, fontWeight: 650 }}
              />
            </>
          ) : (
            <>
              <Stack direction="row" spacing={0.6} alignItems="center" justifyContent="flex-end">
                <Dot color={band ? band.dot : C.line} />
                <Typography sx={{ fontFamily: FONT, fontSize: 15, fontWeight: 750, color: C.ink }}>
                  {row.fit_score != null ? row.fit_score : "—"}
                  <Box component="span" sx={{ color: C.faint, fontWeight: 500, fontSize: 11 }}>/10</Box>
                </Typography>
              </Stack>
              <Meta sx={{ fontSize: 11, color: C.faint }}>{band ? band.label : "Not read"}</Meta>
            </>
          )}
        </Box>
      </Stack>

      {/* What the resume says, the way a job board lays it out. */}
      {/* Indented under the name from a tablet up. Not on a phone: the action
          cluster is a fixed 302px, and the indent is what would push it off a
          360px screen. */}
      <Stack spacing={0.6} sx={{ pl: { xs: 0, sm: 4 } }}>
        {/* The resume reader's own two-line read, first: it is the quickest
            answer to "who is this". Cut at two lines; the whole of it is on
            hover and on their profile. */}
        {row.summary && (
          <Typography
            title={row.summary}
            sx={{
              fontFamily: FONT,
              fontSize: 12.5,
              lineHeight: 1.6,
              color: C.body,
              mb: 0.4,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {row.summary}
          </Typography>
        )}
        <Fact label="Worked at">{employers.join(" · ")}</Fact>
        <Fact label="Education">{education.join(" · ")}</Fact>
        <Fact label="Key skills">{skills.join(" | ")}</Fact>
        <Fact label="Projects">{projects.join(" · ")}</Fact>
        <Fact label="Certified">{certifications.join(" · ")}</Fact>
        {reqs.length > 0 && (
          <Fact label="Job fit">
            <Box component="span" sx={{ fontWeight: 650, color: C.ink }}>
              Meets {reqsMet} of {reqs.length} requirements
            </Box>
            {reqsMissing.length > 0 && (
              <Box component="span" sx={{ color: "#b45309" }}>
                {" "}· missing: {reqsMissing.join(", ")}
              </Box>
            )}
          </Fact>
        )}
      </Stack>

      {/* The two best and two worst things the resume showed. Green and
          amber only here, where they genuinely oppose — as in the row's
          expanded panel. */}
      {(pros.length > 0 || cons.length > 0) && (
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={{ xs: 0.75, sm: 2 }}
          sx={{ pl: { xs: 0, sm: 4 } }}
        >
          {[
            { items: pros, color: "#059669", mark: "+" },
            { items: cons, color: "#b45309", mark: "–" },
          ]
            .filter((g) => g.items.length)
            .map((g) => (
              <Stack key={g.mark} spacing={0.4} sx={{ flex: 1, minWidth: 0 }}>
                {g.items.map((t, i) => (
                  <Stack key={i} direction="row" spacing={0.75} alignItems="flex-start">
                    <Typography sx={{ fontFamily: FONT, fontSize: 12.5, fontWeight: 800, color: g.color, lineHeight: 1.5 }}>
                      {g.mark}
                    </Typography>
                    <Typography sx={{ fontFamily: FONT, fontSize: 12, color: C.body, lineHeight: 1.5 }}>
                      {t}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            ))}
        </Stack>
      )}

      {/* Contact, the WhatsApp step, and the same actions as the row. */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        alignItems={{ sm: "center" }}
        sx={{ pl: { xs: 0, sm: 4 }, pt: 1, borderTop: `1px solid ${C.hair}` }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Meta sx={{ color: C.body }}>
            {[row.phone, row.email].filter(Boolean).join("  ·  ") || "No phone or email on file"}
          </Meta>
          {/* Where the AI interview stands, once there is one. */}
          {row.invited && (
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.35 }}>
              <Dot color={state.dot} pulse={state.key === "LIVE"} />
              <Meta sx={{ fontSize: 11 }}>
                AI interview: {state.label}
                {row.invited_at ? ` · link sent ${fmtDate(row.invited_at)}` : ""}
              </Meta>
            </Stack>
          )}
          {(prescreen || waNote) && (
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              onClick={(e) => onEditWhatsApp?.(e.currentTarget, row)}
              sx={{ mt: 0.35, minWidth: 0, cursor: "pointer" }}
            >
              {prescreen && (
                <>
                  <Dot color={waState.dot} />
                  <Meta sx={{ fontSize: 11, color: row.whatsapp?.status ? C.body : C.faint, flexShrink: 0 }}>
                    {waState.label}
                    {row.whatsapp?.call_slot ? ` · ${row.whatsapp.call_slot}` : ""}
                  </Meta>
                </>
              )}
              {waNote && (
                <>
                  <StickyNote2OutlinedIcon sx={{ fontSize: 12, color: C.faint, flexShrink: 0, ml: prescreen ? 0.75 : 0 }} />
                  <Meta sx={{ fontSize: 11, color: C.muted, fontStyle: "italic" }} title={waNote}>
                    {waNote}
                  </Meta>
                </>
              )}
            </Stack>
          )}
        </Box>
        <Stack direction="row" alignItems="center" justifyContent="flex-end" sx={{ flexShrink: 0 }}>
          {row.resume_url && (
            <Tooltip title="Open the resume">
              <IconButton
                size="small"
                onClick={() => window.open(row.resume_url, "_blank", "noopener")}
                sx={{ p: 0.5 }}
              >
                <DescriptionOutlinedIcon sx={{ fontSize: 16, color: C.muted }} />
              </IconButton>
            </Tooltip>
          )}
          <RowActions row={row} mode={mode} busy={busy} held={held} prescreen={prescreen} {...actionProps} />
        </Stack>
      </Stack>
    </Box>
  );
};

// ── One row ─────────────────────────────────────────────────────────────────

const Row = ({
  row,
  mode,
  index,
  busy,
  checked,
  selectable,
  onCheck,
  onPrimary,
  onReview,
  onReject,
  onUnreject,
  onDelete,
  onOpen,
  onSaveDetail,
  onCopyLink,
  onWhatsApp,
  open,
  onToggle,
  // The row you are working on — last clicked, edited or opened. Marked so
  // the eye lands on it, and `onActivate` moves the mark here.
  highlight,
  onActivate,
  // From the most recent upload.
  isNew,
  // The WhatsApp pre-screen is on for this row's round: a chat button, a
  // status line under the phone number, and `held` while they have not agreed.
  prescreen,
  held,
  onPrescreenChat,
  onEditWhatsApp,
  onSaveNote,
  onDismissVerdict,
}) => {
  // The name is edited in place, but its click belongs to the drawer — so the
  // pencil beside it drives this instead.
  const [editingName, setEditingName] = useState(false);
  // A real address for the profile, so the name is a real link: Ctrl-click,
  // middle-click and "open in new tab" all work, as they do on any other link.
  const profileHref = useHref(`/ats/candidate/${row.application_id}`);
  const state = interviewState(row);
  const band = BAND[row.band];
  const verdict = VERDICT[row.interview_verdict];
  const hasDetail =
    row.screened || row.parse_error || (row.requirements || []).length > 0;
  const rejected = row.status === "REJECTED";
  const interviewed = row.interview_scored && row.interview_score != null;

  // Which date this row leads with. The tab you are on decides what you came
  // here to know: on the rejected list it is when we said no, on the AI round
  // it is when the link went out, everywhere else it is when they applied.
  const leadDate = (() => {
    if (rejected && row.rejected_at) return { label: "Rejected", at: row.rejected_at };
    if (mode === "AI_INTERVIEW" && row.invited_at)
      return { label: "Link sent", at: row.invited_at };
    if (mode === "SCREENING" && row.screened_at)
      return { label: "Screened", at: row.screened_at };
    return { label: "Applied", at: row.applied_at };
  })();

  const inStageDays = daysSince(row.stage_entered_at);
  const waState = WA_STATUS[row.whatsapp?.status ?? null] || WA_STATUS.null;
  const waNote = row.whatsapp?.note || null;

  return (
    <Box
      id={`cand-row-${row.application_id}`}
      // Capture phase, so the mark moves whatever on the row was clicked or
      // tabbed to — a phone number being copied, a cell being edited — and
      // before that control's own handler runs.
      onPointerDownCapture={() => onActivate?.(row.application_id)}
      onFocusCapture={() => onActivate?.(row.application_id)}
      sx={{
        bgcolor: checked ? C.accentSoft : C.card,
        border: `1px solid ${checked ? "#c7d2fe" : open ? C.line : C.hair}`,
        // A bar down the left edge rather than a fill: the fill already means
        // "ticked", and this row is not ticked, only remembered.
        boxShadow: highlight ? `inset 3px 0 0 ${C.accent}` : "none",
        borderRadius: "8px",
        mb: 0.5,
        overflow: "hidden",
        opacity: rejected ? 0.72 : 1,
        animation: `pipeIn .3s ease ${Math.min(index, 8) * 0.025}s both`,
        transition: "border-color .16s, background .16s, box-shadow .16s",
        "&:hover": {
          opacity: 1,
          borderColor: checked ? "#a5b4fc" : C.line,
          boxShadow: highlight
            ? `inset 3px 0 0 ${C.accent}, 0 1px 8px rgba(24,24,27,.05)`
            : "0 1px 8px rgba(24,24,27,.05)",
          // The name's edit pencil. Revealed on row hover rather than always
          // shown: 400 permanent pencils to serve the handful of rows the
          // resume reader got wrong is not a trade worth making.
          "& .row-edit-name": { opacity: 0.9 },
        },
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: GRID,
          alignItems: "center",
          gap: `${GAP}px`,
          px: `${PAD}px`,
          py: 0.85,
        }}
      >
        {/* 1 — select */}
        <Box sx={{ display: "grid", placeItems: "center" }}>
          {selectable && (
            <Checkbox
              size="small"
              checked={checked}
              onChange={() => onCheck(row.application_id)}
              sx={{ p: 0.25, color: C.line, "&.Mui-checked": { color: C.accent } }}
            />
          )}
        </Box>

        {/* 2 — who. Name, and the address under it.
            They sat side by side while the note also lived in this cell, to
            keep it from going three lines tall. With the note in its own
            column, stacked costs no height — score, dates and phone are two
            lines already — and lets names and addresses each line up down the
            list instead of every address starting wherever its name ended.

            The name is also the way into the full profile. It used to be a
            separate person icon in the action cluster — a whole column on every
            row to do what clicking somebody's name already looks like it does.

            ── EDITING THE NAME ──────────────────────────────────────────────
            The resume reader gets names wrong often enough that "(unknown)" is
            a real value in this column: a two-column CV, a name in a header
            image, a name it read as a job title. So the name is editable — but
            NOT by clicking it, because that click already opens the drawer and
            taking it away would break the way into a candidate's profile and
            their history.
            The pencil appears on row hover instead. A permanent icon on every
            row would put 400 pencils on a page to serve the handful of rows
            that need one. */}
        <Box sx={{ minWidth: 0 }}>
        <Stack direction="row" spacing={0.75} alignItems="baseline" sx={{ minWidth: 0 }}>
          {editingName ? (
            <EditableCell
              value={row.candidate_name}
              startOpen
              onClose={() => setEditingName(false)}
              maxLength={255}
              title="The candidate's name"
              onSave={(v) => onSaveDetail(row, { full_name: v })}
            />
          ) : (
            <>
              <Typography
                // An <a>, opening in a new tab on the web — reading a profile
                // no longer throws away the list you were working down. In
                // the phone app onOpen cancels this and navigates instead.
                component={onOpen ? "a" : "p"}
                {...(onOpen
                  ? {
                      href: profileHref,
                      target: "_blank",
                      rel: "noopener",
                      onClick: (e) => onOpen(row, e),
                      // Middle-click opens a tab without firing onClick.
                      onAuxClick: (e) => e.button === 1 && onOpen(row, e),
                    }
                  : {})}
                title={row.candidate_name}
                sx={{
                  fontFamily: FONT,
                  fontWeight: 650,
                  fontSize: 13,
                  color: rejected ? C.muted : C.ink,
                  // Struck through, so the state is legible without relying on
                  // colour alone — the row is already faded, and two signals
                  // that both mean "greyed out" are one signal.
                  textDecoration: rejected ? "line-through" : "none",
                  // Shrinks before the NEW tag and warning beside it, but never
                  // below a readable stub — the name is what you scan for.
                  flexShrink: 1,
                  minWidth: 72,
                  // Block, as the <p> it used to be. An inline <a> ignores the
                  // ellipsis noWrap sets, and a long name would push the email
                  // out of the cell instead of being cut short.
                  display: "block",
                  cursor: onOpen ? "pointer" : "default",
                  "&:hover": onOpen ? { color: C.accent } : undefined,
                }}
                noWrap
              >
                {row.candidate_name}
              </Typography>

              <Tooltip title="Correct the name">
                <EditOutlinedIcon
                  onClick={(e) => {
                    // Or the drawer opens instead — the name beside it is a
                    // click target too.
                    e.stopPropagation();
                    setEditingName(true);
                  }}
                  className="row-edit-name"
                  sx={{
                    fontSize: 13,
                    color: C.faint,
                    cursor: "pointer",
                    flexShrink: 0,
                    alignSelf: "center",
                    // Hidden until the row is hovered, and always visible when
                    // the reader could not be read — that row is the whole
                    // reason this exists.
                    opacity: row.candidate_name === "(unknown)" ? 0.9 : 0,
                    transition: "opacity .15s, color .15s",
                    "&:hover": { color: C.accent },
                  }}
                />
              </Tooltip>
            </>
          )}

          {row.parse_status === "NEEDS_MANUAL_REVIEW" && (
            <Tooltip title={row.parse_error || "The resume could not be read"}>
              <WarningAmberRoundedIcon
                sx={{ fontSize: 13, color: "#d97706", flexShrink: 0, alignSelf: "center" }}
              />
            </Tooltip>
          )}

          {/* From the latest upload — so a new batch is findable however the
              list happens to be sorted. */}
          {isNew && (
            <Box
              component="span"
              sx={{
                flexShrink: 0,
                alignSelf: "center",
                px: 0.6,
                borderRadius: 1,
                fontFamily: FONT,
                fontSize: 9.5,
                fontWeight: 800,
                letterSpacing: 0.4,
                lineHeight: "16px",
                color: C.accent,
                bgcolor: C.accentSoft,
              }}
            >
              NEW
            </Box>
          )}
        </Stack>

          {/* Click to edit. Nothing else claims this click, and an address
              the reader got wrong is the difference between a candidate being
              invited and appearing to have ignored you — the interview link is
              emailed to exactly this value. */}
          <Box sx={{ mt: 0.35, minWidth: 0 }}>
            <EditableCell
              value={row.email}
              placeholder="no email"
              title={
                row.email
                  ? `${row.email} — click to correct it`
                  : "No email address. The interview link is sent here, so click to add one."
              }
              maxLength={255}
              onSave={(v) => onSaveDetail(row, { email: v })}
            />
          </Box>
        </Box>

        {/* 3 — phone. Editable for the same reason as the email: a number split
            across a line break in the CV comes back as half a number, and this
            is what the WhatsApp button dials. */}
        <Box sx={{ minWidth: 0 }}>
          <EditableCell
            value={row.phone}
            placeholder="—"
            title={
              row.phone
                ? `${row.phone} — click to correct it`
                : "No phone number. Click to add one."
            }
            maxLength={20}
            onSave={(v) => onSaveDetail(row, { phone: v })}
          />
          {/* Where the WhatsApp pre-screen stands, under the number it was
              sent to. Click to record the reply, the call time and a note —
              WhatsApp Web cannot tell this page what the candidate said. */}
          {/* No hover text: it covered the row below. The pointer cursor and
              hover shade say it is clickable. */}
          {prescreen && (
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              onClick={(e) => onEditWhatsApp?.(e.currentTarget, row)}
              sx={{
                mt: 0.35,
                px: 0.75,
                minWidth: 0,
                cursor: "pointer",
                borderRadius: "6px",
                "&:hover": { bgcolor: C.wash },
              }}
            >
              <Dot color={waState.dot} />
              <Meta sx={{ fontSize: 10.5, color: row.whatsapp?.status ? C.body : C.faint }}>
                {waState.short}
                {row.whatsapp?.call_slot ? ` · ${row.whatsapp.call_slot}` : ""}
              </Meta>
            </Stack>
          )}
        </Box>

        {/* 4 — experience. Free text, because "6 months", "2.5 yrs" and
            "fresher" are all real answers and forcing them into a number would
            lose the only one of the three that is unambiguous. */}
        <EditableCell
          value={row.experience}
          placeholder={
            // What the resume claimed, greyed, where nobody has entered
            // anything. A starting point, not an answer — the point of the
            // column is that the CV is often wrong about this.
            row.years_experience != null ? `${row.years_experience}y?` : "—"
          }
          title={
            row.years_experience != null
              ? `The resume says ${row.years_experience} years. Click to record what they told you.`
              : "Click to record their experience"
          }
          onSave={(v) => onSaveDetail(row, { experience: v })}
        />

        {/* 5 — notice, in days. Zero is a value, and it means something good. */}
        <EditableCell
          value={row.notice_period_days}
          display={
            Number(row.notice_period_days) === 0 ? "Immediate" : row.notice_period_days
          }
          suffix={Number(row.notice_period_days) === 0 ? "" : "d"}
          numeric
          maxLength={3}
          placeholder="—"
          title="Notice period in days. 0 means they can start immediately."
          onSave={(v) => onSaveDetail(row, { notice_period_days: v })}
        />

        {/* 6 — expected salary, annual. The unit is fixed and stated rather than
            guessed at: monthly and annual are a factor of twelve apart and both
            get typed into a box labelled "salary". */}
        <EditableCell
          value={row.expected_salary_annual}
          display={fmtSalary(row.expected_salary_annual)}
          suffix="/yr"
          numeric
          placeholder="—"
          title={
            fmtSalaryFull(row.expected_salary_annual) ||
            "Expected annual CTC. Click to set."
          }
          onSave={(v) => onSaveDetail(row, { expected_salary_annual: v })}
        />

        {/* Note — typed straight into the cell, like the three before it. It
            is the same note the WhatsApp popover edits, saved on its own so
            the WhatsApp status is left alone. A long note is cut to one line;
            the whole of it shows on hover. */}
        <Tooltip
          title={waNote ? <Box sx={{ whiteSpace: "pre-wrap" }}>{waNote}</Box> : ""}
          placement="top-start"
          enterDelay={400}
        >
          <Box sx={{ minWidth: 0 }}>
            <EditableCell
              value={waNote}
              placeholder="—"
              maxLength={1000}
              title={waNote ? "Click to edit the note" : "Click to add a note"}
              onSave={(v) => onSaveNote(row, v)}
            />
          </Box>
        </Tooltip>

        {/* 7 — one score column, not two.
            The resume rating and the interview score were separate columns, and
            for any given candidate exactly one of them was ever the answer —
            the other was a dash or, worse, a stale number being read as current.
            Whichever is authoritative shows, and the row says which. Once
            somebody has interviewed, that is the interview: it came from
            recorded answers marked against anchors a human wrote, and it
            outranks a rating of their CV. */}
        <Box sx={{ minWidth: 0 }}>
          {interviewed ? (
            <>
              <Stack direction="row" spacing={0.5} alignItems="baseline">
                <Typography
                  sx={{ fontFamily: FONT, fontSize: 13, fontWeight: 700, color: C.ink }}
                >
                  {Math.round(Number(row.interview_score))}
                  <Box component="span" sx={{ color: C.faint, fontWeight: 500, fontSize: 11 }}>
                    /100
                  </Box>
                </Typography>
              </Stack>
              {/* The verdict under the score, as on the card — beside it,
                  "Suggest reject" does not fit the column. */}
              <VerdictLabel
                row={row}
                verdict={verdict}
                onDismiss={onDismissVerdict}
                sx={
                  verdict
                    ? { fontSize: 10.5, color: verdict.fg, fontWeight: 650 }
                    : { fontSize: 10.5, color: C.faint }
                }
              />
            </>
          ) : (
            <>
              <Stack direction="row" spacing={0.6} alignItems="center">
                <Dot color={band ? band.dot : C.line} />
                {row.fit_score != null ? (
                  <Typography
                    sx={{ fontFamily: FONT, fontSize: 13, fontWeight: 700, color: C.ink }}
                  >
                    {row.fit_score}
                    <Box
                      component="span"
                      sx={{ color: C.faint, fontWeight: 500, fontSize: 11 }}
                    >
                      /10
                    </Box>
                  </Typography>
                ) : (
                  <Meta sx={{ fontSize: 12, color: C.faint }}>Not read</Meta>
                )}
              </Stack>
              {/* Below the number: the band while they are waiting to be
                  invited, the interview state once they have been. Both answer
                  "what happens to this person next", which is the question the
                  number alone cannot. */}
              {row.invited ? (
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Dot color={state.dot} pulse={state.key === "LIVE"} />
                  <Meta sx={{ fontSize: 10.5 }}>{state.label}</Meta>
                </Stack>
              ) : (
                <Meta sx={{ fontSize: 10.5, color: C.faint }}>
                  {band ? band.label : "resume"}
                </Meta>
              )}
            </>
          )}
        </Box>

        {/* 8 — dates. One leading date on the row and the whole trail on hover,
            because "when did we screen this" is a glance and "what has actually
            happened to this person" is a question you ask about one candidate. */}
        <Tooltip
          placement="left"
          title={
            <Box sx={{ py: 0.5 }}>
              <Typography sx={{ fontSize: 11.5, fontWeight: 700, mb: 0.75 }}>
                History
              </Typography>
              {(row.timeline || []).length ? (
                row.timeline
                  .slice()
                  .reverse()
                  .slice(0, 12)
                  .map((t, i) => (
                    <Typography key={i} sx={{ fontSize: 11.5, lineHeight: 1.7 }}>
                      {fmtDateTime(t.at)} — {t.label}
                      {t.to_stage ? ` → ${t.to_stage}` : ""}
                    </Typography>
                  ))
              ) : (
                <Typography sx={{ fontSize: 11.5 }}>
                  Nothing recorded beyond the application.
                </Typography>
              )}
              {/* Points at where the real trail lives. This card is a summary
                  and cannot be more than one: it is built for every row at
                  once, so it carries no operator name and no notes. The full
                  log — who did what, and what they said — is the Log & History
                  tab in the candidate drawer, opened by clicking the name. */}
              <Typography sx={{ fontSize: 11, mt: 0.75, opacity: 0.7, fontStyle: "italic" }}>
                Click the name for the full log, with notes and who acted.
              </Typography>
              {inStageDays != null && (
                <Typography sx={{ fontSize: 11.5, mt: 0.75, opacity: 0.8 }}>
                  {inStageDays === 0
                    ? "Entered this round today"
                    : `${inStageDays} day${inStageDays === 1 ? "" : "s"} in this round`}
                </Typography>
              )}
            </Box>
          }
        >
          <Box sx={{ minWidth: 0, cursor: "help" }}>
            <Typography
              sx={{ fontFamily: FONT, fontSize: 12, color: C.body, lineHeight: 1.35 }}
              noWrap
            >
              {fmtDate(leadDate.at) || "—"}
            </Typography>
            <Meta sx={{ fontSize: 10.5, color: C.faint }}>{leadDate.label}</Meta>
          </Box>
        </Tooltip>

        {/* 9 — the CV.
            Its own cell rather than the fourth of six grey icons. Opening the
            resume is the most common thing anybody does to one of these rows,
            and it was indistinguishable at a glance from "delete permanently". */}
        <Box sx={{ display: "grid", placeItems: "center" }}>
          {row.resume_url ? (
            <Tooltip title="Open the resume">
              <IconButton
                size="small"
                onClick={() => window.open(row.resume_url, "_blank", "noopener")}
                sx={{
                  p: 0.6,
                  border: `1px solid ${C.line}`,
                  borderRadius: "6px",
                  "&:hover": { borderColor: C.accent, bgcolor: C.accentSoft },
                }}
              >
                <DescriptionOutlinedIcon sx={{ fontSize: 16, color: C.muted }} />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="No resume on file">
              <Meta sx={{ color: C.line }}>—</Meta>
            </Tooltip>
          )}
        </Box>

        {/* 10 — actions */}
        <Stack direction="row" spacing={0} alignItems="center" justifyContent="flex-end">
          <RowActions
            row={row}
            mode={mode}
            busy={busy}
            held={held}
            prescreen={prescreen}
            onPrimary={onPrimary}
            onReview={onReview}
            onReject={onReject}
            onUnreject={onUnreject}
            onDelete={onDelete}
            onCopyLink={onCopyLink}
            onWhatsApp={onWhatsApp}
            onPrescreenChat={onPrescreenChat}
          />

          <Box sx={{ width: 22, display: "grid", placeItems: "center", flexShrink: 0 }}>
            {hasDetail && (
              <Tooltip title={open ? "Hide what the resume said" : "What the resume said"}>
                <IconButton size="small" sx={{ p: 0.25 }} onClick={() => onToggle(row.application_id)}>
                  <KeyboardArrowDownIcon
                    sx={{
                      fontSize: 17,
                      color: C.faint,
                      transition: "transform .22s",
                      transform: open ? "rotate(180deg)" : "none",
                    }}
                  />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Stack>
      </Box>

      <Collapse in={open} unmountOnExit>
        <Divider sx={{ borderColor: C.hair }} />
        <Box sx={{ px: 3, py: 3, bgcolor: C.wash }}>
          {row.parse_error && (
            <Alert
              severity="warning"
              sx={{ mb: 2.5, borderRadius: 2.5, fontFamily: FONT, fontSize: 13 }}
            >
              {row.parse_error}
            </Alert>
          )}

          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <AutoAwesomeIcon sx={{ fontSize: 14, color: C.accent }} />
            <Typography
              sx={{
                fontFamily: FONT,
                fontSize: 11,
                fontWeight: 750,
                letterSpacing: 0.8,
                color: C.accent,
              }}
            >
              READ FROM THE RESUME
            </Typography>
            {row.screened_at && (
              <Typography sx={{ fontFamily: FONT, fontSize: 11, color: C.faint }}>
                · {fmtDateTime(row.screened_at)}
              </Typography>
            )}
          </Stack>

          {row.summary && (
            <Typography
              sx={{
                fontFamily: FONT,
                fontSize: 14,
                lineHeight: 1.75,
                color: C.body,
                mb: 3,
                maxWidth: 720,
              }}
            >
              {row.summary}
            </Typography>
          )}

          <Stack direction={{ xs: "column", lg: "row" }} spacing={5}>
            {(row.requirements || []).length > 0 && (
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontFamily: FONT,
                    fontSize: 11,
                    fontWeight: 750,
                    letterSpacing: 0.7,
                    color: C.faint,
                    mb: 1.5,
                  }}
                >
                  REQUIREMENTS
                </Typography>
                {row.requirements.map((r, i) => {
                  const m = MET[r.met] || MET.UNCLEAR;
                  return (
                    <Box key={i} sx={{ mb: 1.75 }}>
                      <Stack direction="row" spacing={1.25} alignItems="flex-start">
                        <Box
                          sx={{
                            width: 18,
                            height: 18,
                            borderRadius: "50%",
                            display: "grid",
                            placeItems: "center",
                            flexShrink: 0,
                            mt: "1px",
                            color: m.color,
                            border: `1px solid ${m.color}22`,
                            bgcolor: `${m.color}0f`,
                          }}
                        >
                          {m.icon}
                        </Box>
                        <Typography
                          sx={{
                            fontFamily: FONT,
                            fontSize: 13.5,
                            lineHeight: 1.6,
                            color: C.body,
                          }}
                        >
                          {r.requirement}
                        </Typography>
                      </Stack>
                      <Box sx={{ pl: 3.75 }}>
                        <Evidence text={r.evidence} />
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}

            <Box sx={{ flex: 1, minWidth: 0 }}>
              {(row.strengths || []).length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography
                    sx={{
                      fontFamily: FONT,
                      fontSize: 11,
                      fontWeight: 750,
                      letterSpacing: 0.7,
                      color: "#059669",
                      mb: 1.5,
                    }}
                  >
                    PROS
                  </Typography>
                  {row.strengths.map((s, i) => (
                    <Box key={i} sx={{ mb: 1.75 }}>
                      <Typography
                        sx={{
                          fontFamily: FONT,
                          fontSize: 13.5,
                          lineHeight: 1.6,
                          color: C.body,
                        }}
                      >
                        {s.point}
                      </Typography>
                      <Evidence text={s.evidence} />
                    </Box>
                  ))}
                </Box>
              )}

              {(row.concerns || []).length > 0 && (
                <Box>
                  <Typography
                    sx={{
                      fontFamily: FONT,
                      fontSize: 11,
                      fontWeight: 750,
                      letterSpacing: 0.7,
                      color: "#b45309",
                      mb: 1.5,
                    }}
                  >
                    CONS
                  </Typography>
                  {row.concerns.map((c, i) => (
                    <Box key={i} sx={{ mb: 1.75 }}>
                      <Typography
                        sx={{
                          fontFamily: FONT,
                          fontSize: 13.5,
                          lineHeight: 1.6,
                          color: C.body,
                        }}
                      >
                        {c.point}
                      </Typography>
                      <Evidence text={c.evidence} />
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Stack>

          {row.band_reason && (
            <Typography
              sx={{
                fontFamily: FONT,
                fontSize: 12.5,
                lineHeight: 1.7,
                color: C.faint,
                mt: 3,
                pt: 2.5,
                borderTop: `1px solid ${C.hair}`,
                maxWidth: 720,
              }}
            >
              Why “{band ? band.label : row.band}”: {row.band_reason}
            </Typography>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

// ── The pipeline ────────────────────────────────────────────────────────────

const CandidatePipeline = ({
  jobId,
  onRejectCandidate,
  onUnrejectCandidate,
  onOpenCandidate,
  onChanged,
}) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Read once, on mount. After that the URL follows the state, never the other
  // way round — two sources of truth that update each other is a loop.
  const [initial] = useState(() => readPipelineParams(searchParams));

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Null until somebody picks one. `active` falls back to the first round, so
  // the pipeline opens on Resume Screening — where applicants actually arrive —
  // rather than on a combined list that describes no round and therefore offers
  // no round's actions. This was "ALL", a key that no longer exists.
  //
  // A round named in the URL that this job does not have falls back the same
  // way, because `active` only matches keys that exist.
  const [tab, setTab] = useState(initial.tab);
  const [search, setSearch] = useState(initial.search);
  // The candidate you are working on: the row last clicked, copied from,
  // edited or opened. Their row is marked, and on the way back from a profile
  // the list scrolls to it — "which one was I on" is the question the old
  // Back left you to answer by memory.
  //
  // It follows ANY interaction with a row, not only opening the profile. A
  // marker that stayed on the last profile opened sat on one candidate while
  // you were plainly working on another, which is worse than no marker.
  //
  // Only a marker. It never ticks a checkbox: the ticks drive the bulk
  // actions, and a click that quietly added somebody to "Reject 12" would be
  // a trap.
  const [lastOpenedId, setLastOpenedId] = useState(initial.open);
  const activateRow = useCallback(
    (id) => setLastOpenedId((cur) => (cur === id ? cur : id)),
    [],
  );
  // ── Who has actually sat the interview ────────────────────────────────────
  //
  // Only meaningful on an AI_INTERVIEW round, and the question it answers is
  // the one asked of this tab every single day: who still needs chasing.
  //
  // The buckets are not "done / not done", because "not done" contains two
  // groups with nothing in common. Somebody who was never sent a link needs a
  // link; somebody who was sent one four days ago needs a reminder. Collapsing
  // them into one filter means the list you get cannot be acted on without
  // reading every row's status anyway, which is what the filter was for.
  const [sat, setSat] = useState(initial.sat);
  const SAT_FILTERS = useMemo(
    () => [
      { key: "ALL", label: "All", match: () => true },
      // Nothing sent. The action is "send the link".
      { key: "NOT_SENT", label: "Not invited", match: (k) => k === "NONE" },
      // Sent and not finished — including expired, which is a chase with an
      // extra step rather than a different situation.
      {
        key: "WAITING",
        label: "Awaiting",
        match: (k) => k === "SENT" || k === "LIVE" || k === "EXPIRED",
      },
      // Sat it. SCORING counts: the candidate is finished and it is our
      // scoring they are waiting on, not the other way round.
      {
        key: "DONE",
        label: "Interviewed",
        match: (k) => k === "DONE" || k === "SCORING",
      },
    ],
    [],
  );
  // ── Whose CV has actually been read ────────────────────────────────
  //
  // The Resume Screening round's counterpart to the interview filter above, and
  // it answers the question that round is actually worked from: who is still
  // waiting to be read.
  //
  // Three buckets, not two, for the same reason SAT_FILTERS has four. "Not
  // screened" sounds like one group and is not. A candidate whose resume parsed
  // fine is one click from a rating; one whose file could not be read will
  // never get a rating however many times the button is pressed — they need a
  // different file from the candidate. Collapsed into one list, "Screen the
  // rest" leaves rows behind on every run and nothing on screen says why.
  //
  // The three tests are mutually exclusive on purpose, so Screened +
  // Not screened + Unreadable always equals All. A chip that double-counts is
  // worse than no chip at all.
  const [screenFilter, setScreenFilter] = useState(initial.screenFilter);
  const SCREEN_FILTERS = useMemo(
    () => [
      { key: "ALL", label: "All", match: () => true },
      // A screening result exists for THIS job — a band and a fit score.
      { key: "SCREENED", label: "Screened", match: (r) => Boolean(r.screened) },
      // No result yet, and nothing in the way of one. The action is "Screen".
      {
        key: "PENDING",
        label: "Not screened",
        match: (r) => !r.screened && r.parse_status !== "NEEDS_MANUAL_REVIEW",
      },
      // The resume could not be read at all. Same test as the server's own
      // needs_manual count, which is what the header line already reports —
      // both sides answering from one definition is the point.
      {
        key: "UNREADABLE",
        label: "Unreadable CV",
        match: (r) => !r.screened && r.parse_status === "NEEDS_MANUAL_REVIEW",
      },
    ],
    [],
  );
  // The WhatsApp pre-screen filter (WA_FILTERS). null until somebody picks
  // one, and then the job decides: "To contact" where the pre-screen is on,
  // "All" where it is off. See `waFilterKey` below.
  const [waFilter, setWaFilter] = useState(initial.waFilter);
  // "score" — the resume rating, as always — or "newest", which puts the last
  // upload on top. Not offered on the AI round (interview score) or the
  // rejected list (most recent rejection), which have orders of their own.
  const [sortBy, setSortBy] = useState(initial.sortBy);
  // The job's WhatsApp message editor, and the status/note popover for one row:
  // `{ anchor, row }`.
  const [templateOpen, setTemplateOpen] = useState(false);
  const [waEdit, setWaEdit] = useState(null);
  // The Filters popover (the CV filter and the date range).
  const [filtersAnchor, setFiltersAnchor] = useState(null);
  // Where the pre-screen chat opens on this computer: the Desktop app (instant)
  // or WhatsApp Web (a fresh tab each time). See prescreenMessage.js for why
  // Web cannot reuse a tab.
  const [chatOpener, setChatOpenerState] = useState(getChatOpener);
  const chooseChatOpener = (key) => {
    setChatOpener(key);
    setChatOpenerState(key);
  };

  // Table or cards. Remembered in this browser — a way of reading rather than
  // part of the view being shared, so it stays out of the URL. Wrapped,
  // because storage can be refused (private windows, blocked site data), and
  // that must cost the preference, not the page.
  const [view, setView] = useState(() => {
    try {
      return window.localStorage.getItem(VIEW_STORAGE_KEY) === "cards" ? "cards" : "list";
    } catch {
      return "list";
    }
  });
  useEffect(() => {
    try {
      window.localStorage.setItem(VIEW_STORAGE_KEY, view);
    } catch {
      // Not remembered; nothing else is affected.
    }
  }, [view]);

  const [busy, setBusy] = useState(null);
  const [screening, setScreening] = useState(false);
  const [toast, setToast] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  // The rejection waiting for a reason. `{ row }` for one candidate,
  // `{ ids, count }` for a bulk action -- one piece of state rather than two
  // so the dialog cannot be open for both at once.
  const [pendingReject, setPendingReject] = useState(null);
  // The invite waiting to be confirmed. `{ row, stage }` for one candidate,
  // `{ ids, count, stage }` for a selection. Same one-piece-of-state shape as
  // pendingReject, and for the same reason.
  const [pendingInvite, setPendingInvite] = useState(null);
  // The WhatsApp send waiting to be confirmed: `{ rows }`, one row or many.
  // Same one-piece-of-state shape as the two above.
  const [pendingWhatsApp, setPendingWhatsApp] = useState(null);

  // ── Date range ────────────────────────────────────────────────────────────
  //
  // Sent to the server rather than applied here. The text search is instant and
  // client-side because it runs on every keystroke; a date range is a deliberate
  // act, happens rarely, and has to stay correct on a job with more applicants
  // than the browser should be holding.
  const [dateField, setDateField] = useState(initial.dateField);
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  // What the inputs show, and what has actually been asked for, are two things.
  // A date input fires onChange while a year is still being typed — "0002-01-01"
  // on the way to 2026 — so binding the fetch straight to it means three wasted
  // requests and a list that flashes empty before the real range lands.
  const [range, setRange] = useState({ from: initial.from, to: initial.to });
  useEffect(() => {
    // The same object back when nothing changed. A fresh one with equal values
    // still reads as a new range to everything keyed on it: on mount that was
    // a second fetch 400ms after the first, and it would now also throw away
    // the page number restored from the URL.
    const t = setTimeout(
      () => setRange((r) => (r.from === from && r.to === to ? r : { from, to })),
      400,
    );
    return () => clearTimeout(t);
  }, [from, to]);

  // ── Selection ─────────────────────────────────────────────────────────────
  const [selected, setSelected] = useState(() => new Set());
  const [moveTo, setMoveTo] = useState("");
  const [bulk, setBulk] = useState(null); // { done, total, label }
  // Bumped once per completed fetch, and used as the signal to drop the
  // selection. Watching `data` for that looked equivalent and was not: an
  // inline cell edit writes to `data` optimistically, so typing a notice period
  // silently unticked the eleven other people somebody had just selected.
  const [loadSeq, setLoadSeq] = useState(0);

  // ── One row open at a time ────────────────────────────────────────────────
  //
  // Each expanded row is a screen-and-a-bit of resume evidence. With per-row
  // state you could open six and then be scrolling through six candidates'
  // pros and cons looking for the list again. Opening one closes the last.
  const [expandedId, setExpandedId] = useState(null);
  const toggleExpanded = useCallback(
    (id) => setExpandedId((cur) => (cur === id ? null : id)),
    [],
  );

  // ── Pagination ────────────────────────────────────────────────────────────
  //
  // Client-side, over rows already fetched: the date range and the round are
  // filtered on the server, and what survives both is a working list somebody
  // reads down. Ten at a time because that is roughly a screen — the point is
  // to stop 115 candidates rendering at once, not to page a database.
  // ── The WhatsApp gateway ──────────────────────────────────────────────────
  //
  // Nothing to hold here. The token lives on the server, which reads the
  // store's own from store_settings — including when somebody is acting inside
  // another store's pipeline, where the browser's single token would have sent
  // under the wrong company's name. See services/whatsappService.js.

  const [page, setPage] = useState(initial.page);
  const [perPage, setPerPage] = useState(initial.perPage);

  // `quiet` is the refresh on coming back to this browser tab (see below).
  // An ordinary load drops the selection — the list may have changed under it.
  // A quiet one keeps every ticked candidate who is still here, because
  // glancing at a profile in another tab is not a reason to lose twelve ticks,
  // and it never swaps the list for an error screen over a blip.
  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setError(null);
    try {
      const res = await interviewService.screeningList(jobId, {
        date_field: dateField,
        ...(range.from ? { from: range.from } : {}),
        ...(range.to ? { to: range.to } : {}),
      });
      // The API answers 200 with statusCode in the body, so a failure never
      // reaches a catch. Checking `success` is the only thing that works.
      if (!res.data?.success) {
        if (!quiet) setError(res.data?.statusMessage || "Could not load candidates.");
        return;
      }
      setData(res.data.data);
      if (quiet) {
        const still = new Set((res.data.data?.rows || []).map((r) => r.application_id));
        setSelected((prev) => {
          const kept = [...prev].filter((id) => still.has(id));
          return kept.length === prev.size ? prev : new Set(kept);
        });
      } else {
        setLoadSeq((n) => n + 1);
      }
    } catch (err) {
      if (!quiet) {
        setError(
          err.response?.data?.statusMessage || err.message || "Could not load candidates.",
        );
      }
    } finally {
      setLoading(false);
    }
  }, [jobId, dateField, range]);

  useEffect(() => {
    load();
  }, [load]);

  // Memoised because `|| []` builds a NEW array on every render, which makes it
  // a changing dependency of the tabs useMemo below — so the tab list was being
  // rebuilt on every keystroke in the search box for no reason.
  const stages = useMemo(() => data?.stages || [], [data]);
  // Only the AI round is looked up now — for the "this job cannot issue
  // interview links" alert below. `screeningStage` went with the old hand-built
  // tab list, which needed to know whether a separate screening round existed
  // in order to name the AI tab differently. Tabs now use the job's own round
  // names, so nothing has to guess.
  const interviewStage = stages.find((s) => s.type === "AI_INTERVIEW");
  const jobTitle = data?.job?.title || "the role";
  // What the hand-sent invitation needs from the job: the JD to link to, and
  // the sign-off to end with. Both optional, both read straight off the job so
  // a paste and an email cannot say different things.
  const jobJd = data?.job?.jd_pdf_url
    ? { url: data.job.jd_pdf_url, name: data.job.jd_pdf_name }
    : null;
  const jobSignature = data?.job?.mail_signature || null;
  // null when the job has no AI round at all — a different problem, reported by
  // the alert below this one.
  const rubricState = data?.rubric || null;

  // ── The WhatsApp pre-screen ──────────────────────────────────────────────
  //
  // ON for a job that has a WhatsApp message saved, and only once the server
  // has the tables for it (migration 035). Off, every control below is hidden
  // and the job invites straight from screening exactly as it always has.
  const prescreenOn = Boolean(data?.whatsapp_ready && data?.job?.whatsapp_template);

  // Whether an AI-interview invite must wait for this candidate's yes.
  //
  // Only on the way INTO the AI round. Somebody already in it is never held:
  // Resend, Copy and the AI round's own WhatsApp send work exactly as before,
  // including for candidates invited before the pre-screen was switched on.
  //
  // This is the whole gate, and it lives in the browser on purpose. The
  // server's invite (interviewController.createInvite) is not changed by this
  // feature at all — it rotates tokens, moves rounds and emails strangers, and
  // it is the part of this module that breaks when touched. Every path in this
  // file that can invite somebody checks this first.
  const heldForWhatsApp = (row) =>
    prescreenOn &&
    !(interviewStage && row.current_stage_id === interviewStage.id) &&
    !WA_CLEARED.has(row.whatsapp?.status);

  // Tabs are semantic, not raw stage names. "All Stages" said nothing about what
  // you would find there or what you could do about it.
  // ── One tab per round of THIS job, in the job's own order ────────────────
  //
  // Previously the tabs were a hand-built list: an "All" tab, then two
  // specially-named entries for the screening and AI rounds, then whatever
  // stages were left over. That had three problems:
  //
  //   * "All" mixed six rounds into one list, so the actions on a row depended
  //     on a tab that described no particular round — and it was the default,
  //     which is where people landed;
  //   * the screening and AI rounds were renamed ("New applicants", "AI
  //     screening") so the tab did not match the round name the same person had
  //     typed in the job wizard;
  //   * ordering was tabs-first rather than pipeline order, so a job's rounds
  //     did not read left-to-right in the order a candidate moves through them.
  //
  // Now: the rounds, named as the job names them, in stage order. What a row
  // can DO comes from the round's TYPE, not from the tab's position, so a
  // renamed round keeps its actions.
  const tabs = useMemo(() => {
    const list = stages.map((s) => ({
      key: `STAGE_${s.id}`,
      label: s.name,
      stageId: s.id,
      stageType: s.type,
    }));

    // ── The safety tab ─────────────────────────────────────────────────────
    //
    // Every tab above filters on a stage id, so a live candidate sitting on no
    // stage — or on one belonging to another job — would appear on NO tab and
    // silently disappear from the pipeline. "All" used to catch them by
    // accident.
    //
    // Only appears when there is somebody in it, so it is a fault report rather
    // than a permanent tab nobody needs. Zero such rows today; a job created
    // with no rounds at all is how it happens, because the intake writes
    // whatever the first round is and there is none.
    const known = new Set(stages.map((s) => s.id));
    const stranded = (data?.rows || []).filter(
      (r) => r.status !== "REJECTED" && !known.has(r.current_stage_id),
    );
    if (stranded.length) {
      list.push({ key: "UNPLACED", label: "Not in any round", stageId: null, unplaced: true });
    }

    // Last, and its own list.
    //
    // Rejected candidates used to sit faded at the bottom of every tab, which
    // kept a misclick visible at the cost of putting people who are out of the
    // running in front of you on every screen — and on a job with a hundred
    // applicants the fade stopped being enough. They get their own tab, and the
    // undo lives on the row and in the toast.
    list.push({ key: "REJECTED", label: "Rejected", stageId: null, rejected: true });
    return list;
  }, [stages, data]);

  const active = tabs.find((t) => t.key === tab) || tabs[0];

  // What a row can do here, decided by the ROUND'S TYPE.
  //
  //   SCREENING     -> "Invite to AI Interview" (moves, issues the link, emails)
  //   AI_INTERVIEW  -> "Send link" / "Resend"
  //   anything else -> no primary action; candidates are moved with the bulk
  //                    control or from the drawer
  //
  // Keyed on type rather than on the tab, so renaming "Resume Screening" to
  // "CV sift" in the wizard does not take its button away.
  const mode =
    active?.key === "REJECTED"
      ? "REJECTED"
      : active?.stageType === "AI_INTERVIEW"
        ? "AI_INTERVIEW"
        : active?.stageType === "SCREENING"
          ? "SCREENING"
          : "OTHER";

  // Which candidates belong on a given tab. Used both for the list and for the
  // counts, so a tab can never claim a number the list does not show.
  const rowsForTab = useCallback(
    (t) => {
      const list = data?.rows || [];
      if (t?.rejected) return list.filter((r) => r.status === "REJECTED");
      const live = list.filter((r) => r.status !== "REJECTED");
      if (t?.stageId) return live.filter((r) => r.current_stage_id === t.stageId);
      // The safety tab: everyone the round tabs cannot show. See `tabs`.
      if (t?.unplaced) {
        const known = new Set((data?.stages || []).map((s) => s.id));
        return live.filter((r) => !known.has(r.current_stage_id));
      }
      return live;
    },
    [data],
  );

  // Everything the round and the search leave, BEFORE the interview filter.
  // Split out so the filter chips can be counted against it — a chip reading
  // "Awaiting 0" is what tells you there is nobody to chase, and a chip
  // counted against its own filtered output would always read its own total.
  const tabRows = useMemo(() => {
    let list = rowsForTab(active);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((r) => matchesSearch(r, q));
    }
    return list;
  }, [rowsForTab, active, search]);

  // The same search, run on every OTHER round. Shown under the list as
  // read-only lines with a way into that round — never as full rows here,
  // because a row's buttons belong to its round: a candidate from the HR round
  // shown on Resume Screening would be offered "Invite to AI Interview".
  const otherRoundMatches = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    return tabs
      .filter((t) => t.key !== active?.key)
      .map((t) => ({ tab: t, rows: rowsForTab(t).filter((r) => matchesSearch(r, q)) }))
      .filter((g) => g.rows.length);
  }, [tabs, active, rowsForTab, search]);
  const otherRoundTotal = otherRoundMatches.reduce((n, g) => n + g.rows.length, 0);

  // Into the candidate's own round, search kept, landing on their row.
  const openInRound = (t, row) => {
    setTab(t.key);
    activateRow(row.application_id);
    setTimeout(() => {
      document
        .getElementById(`cand-row-${row.application_id}`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 80);
  };

  // Only on an interview round. Elsewhere the state means nothing, so the
  // filter is inert rather than hidden-but-applied — switching to Resume
  // Screening with "Interviewed" left selected must not silently empty it.
  const onInterviewRound = active?.stageType === "AI_INTERVIEW";

  // Same rule as onInterviewRound above: off this round the buckets describe
  // nothing, so the filter is neither drawn nor applied. Leaving it applied
  // while hidden is how a list comes up short with nothing explaining it.
  const onScreeningRound = active?.stageType === "SCREENING";

  const satCounts = useMemo(() => {
    const out = {};
    for (const f of SAT_FILTERS) {
      out[f.key] =
        f.key === "ALL"
          ? tabRows.length
          : tabRows.filter((r) => f.match(interviewState(r).key)).length;
    }
    return out;
  }, [tabRows, SAT_FILTERS]);

  // Counted against tabRows, not against the filtered list, for the reason
  // given on tabRows: a chip counting its own output would always read its own
  // total, and "Not screened 0" is exactly the answer worth being able to see.
  const screenCounts = useMemo(() => {
    const out = {};
    for (const f of SCREEN_FILTERS) {
      out[f.key] =
        f.key === "ALL" ? tabRows.length : tabRows.filter(f.match).length;
    }
    return out;
  }, [tabRows, SCREEN_FILTERS]);

  // The pre-screen filter in force. Inert off the screening round or with the
  // pre-screen off — never hidden-but-applied, for the reason given above.
  const waFilterOn = onScreeningRound && prescreenOn;
  const waFilterKey = waFilterOn ? waFilter ?? "TO_CONTACT" : "ALL";

  const waCounts = useMemo(() => {
    const out = {};
    for (const f of WA_FILTERS) {
      out[f.key] = tabRows.filter((r) => f.match(r.whatsapp?.status || null)).length;
    }
    return out;
  }, [tabRows]);

  // ── Uploads, as batches ──────────────────────────────────────────────────
  //
  // Arrivals grouped into uploads: walking newest-first, a gap longer than
  // BATCH_WINDOW_MS starts the next upload. 0 is the latest. Worked out from
  // the server's arrival times rather than from what this browser saw last,
  // so it says the same thing on every machine.
  const uploadRank = useMemo(() => {
    const timed = (data?.rows || [])
      .map((r) => ({ id: r.application_id, t: new Date(r.applied_at || 0).getTime() }))
      .filter((x) => Number.isFinite(x.t) && x.t > 0)
      .sort((a, b) => b.t - a.t);
    const rank = new Map();
    let batch = 0;
    for (let i = 0; i < timed.length; i++) {
      if (i > 0 && timed[i - 1].t - timed[i].t > BATCH_WINDOW_MS) batch++;
      rank.set(timed[i].id, batch);
    }
    return rank;
  }, [data]);

  // The latest upload, marked "New" so a fresh batch is obvious however the
  // list is sorted — while it is recent, and only when the job has more than
  // one upload: "New" on every row distinguishes nothing.
  const latestUpload = useMemo(() => {
    const ids = [...uploadRank].filter(([, b]) => b === 0).map(([id]) => id);
    if (!ids.length || ids.length === uploadRank.size) return new Set();
    const newest = Math.max(
      ...(data?.rows || [])
        .filter((r) => ids.includes(r.application_id))
        .map((r) => new Date(r.applied_at).getTime()),
    );
    return Date.now() - newest > NEW_FOR_MS ? new Set() : new Set(ids);
  }, [uploadRank, data]);

  // While there is search text the chips step aside (see `rows`): "All" shows
  // as selected so the chips match the list, and picking a chip clears the
  // search so the chip does what it says.
  const searching = Boolean(search.trim());
  const chipOn = (selected, key) => (searching ? key === "ALL" : selected === key);
  const pickChip = (setter) => (key) => {
    setter(key);
    if (searching) setSearch("");
  };

  // The sort control, where the resume rating is the order.
  const sortable = active?.stageType !== "AI_INTERVIEW" && active?.key !== "REJECTED";

  const rows = useMemo(() => {
    let list = tabRows;
    // A search looks across the whole round. Somebody typing a name is looking
    // for that person, and "not found" because they sit under a different
    // chip reads as the candidate being missing.
    if (!searching && onInterviewRound && sat !== "ALL") {
      const f = SAT_FILTERS.find((x) => x.key === sat);
      if (f) list = list.filter((r) => f.match(interviewState(r).key));
    }
    if (!searching && onScreeningRound && screenFilter !== "ALL") {
      const f = SCREEN_FILTERS.find((x) => x.key === screenFilter);
      // Takes the row itself rather than a derived key: everything these
      // buckets need is already on the row, and a second state function would
      // be one more place for the definition to drift from the server's.
      if (f) list = list.filter(f.match);
    }
    if (!searching && waFilterKey !== "ALL") {
      const f = WA_FILTERS.find((x) => x.key === waFilterKey);
      if (f) list = list.filter((r) => f.match(r.whatsapp?.status || null));
    }
    return [...list].sort((a, b) => {
      // "Newest upload first", where offered. Then the usual order below,
      // so a batch still reads best-resume-first within itself.
      // By upload, not by second: within one upload the files land seconds
      // apart, and sorting on that would scramble the rating order inside it.
      if (sortable && sortBy === "newest") {
        const ar = uploadRank.get(a.application_id) ?? Infinity;
        const br = uploadRank.get(b.application_id) ?? Infinity;
        if (ar !== br) return ar - br;
      }
      // Interviewed candidates first and in score order: that number came from
      // recorded answers and is the strongest thing known about them.
      // Keyed on the round's TYPE. This read `active.key === "AI_INTERVIEW"`,
      // and tab keys are now `STAGE_<id>` — so the branch never fired and the
      // AI round silently fell back to resume-rating order, burying the
      // candidates who had actually been interviewed.
      if (active?.stageType === "AI_INTERVIEW") {
        const as = a.interview_score ?? -1;
        const bs = b.interview_score ?? -1;
        if (as !== bs) return bs - as;
      }
      // The rejected list is chronological — most recently turned down first.
      // Ordering it by fit score would put the best resume we said no to at the
      // top of the page, every time.
      if (active?.key === "REJECTED") {
        return new Date(b.rejected_at || b.applied_at || 0) -
          new Date(a.rejected_at || a.applied_at || 0);
      }
      // Then the resume rating, highest first. Unrated rows sort last rather
      // than as zero — "not read yet" is not the same as "scored 0".
      const af = a.fit_score ?? -1;
      const bf = b.fit_score ?? -1;
      if (af !== bf) return bf - af;
      // Band as a tiebreak, then most recent.
      const ab = BAND_ORDER[a.band] ?? 3;
      const bb = BAND_ORDER[b.band] ?? 3;
      if (ab !== bb) return ab - bb;
      return new Date(b.applied_at || 0) - new Date(a.applied_at || 0);
    });
  }, [
    tabRows,
    searching,
    active,
    sat,
    onInterviewRound,
    SAT_FILTERS,
    screenFilter,
    onScreeningRound,
    SCREEN_FILTERS,
    waFilterKey,
    sortable,
    sortBy,
    uploadRank,
  ]);

  const counts = data?.counts || {};

  // The slice actually on screen. `rows` stays the full filtered set, because
  // the tab counts, "select all" and the bulk actions all have to mean "what I
  // can see" — and what you can see is one page.
  const pageCount = Math.max(1, Math.ceil(rows.length / perPage));
  // Clamped rather than reset. Deleting the last candidate on page 3 should
  // land you on page 2, not back at the top of the list.
  const safePage = Math.min(page, pageCount);
  const firstIndex = (safePage - 1) * perPage;
  const pageRows = rows.slice(firstIndex, firstIndex + perPage);

  // ── Back to the candidate you were reading ───────────────────────────────
  //
  // Once, after the first list arrives. The page in the URL is where they
  // were, but the list may have moved since — new uploads re-sort it — so the
  // page is worked out from where they are NOW, then the row is scrolled to.
  // Never again after that: jumping the list while somebody is working down
  // it would be worse than the problem this solves.
  const returnedTo = useRef(false);
  useEffect(() => {
    if (returnedTo.current || !data) return;
    // Spent on the first list whether or not there is anybody to return to.
    // Otherwise clicking a name later — which sets lastOpenedId — would fire
    // this and scroll the list out from under the click.
    returnedTo.current = true;
    if (!lastOpenedId) return;
    const at = rows.findIndex((r) => r.application_id === lastOpenedId);
    if (at < 0) return; // rejected, deleted or filtered out since; nothing to find
    const onPage = Math.floor(at / perPage) + 1;
    if (onPage !== safePage) setPage(onPage);
    // After the page change has rendered. Centred, so the rows around it —
    // the ones you were comparing it with — are on screen too.
    setTimeout(() => {
      document
        .getElementById(`cand-row-${lastOpenedId}`)
        ?.scrollIntoView({ block: "center", behavior: "smooth" });
    }, 120);
  }, [data, rows, lastOpenedId, perPage, safePage]);

  // ── Fresh when you come back ─────────────────────────────────────────────
  //
  // The profile opens in its own browser tab, and what is done there — a
  // decision, a rejection, an edited phone number — happens behind this list.
  // Coming back to this tab refetches it quietly: same view, same page, and
  // the ticks kept for everyone still in it. Skipped while a bulk run or a
  // confirmation is in progress, because those hold rows that must not change
  // under them.
  const quietRefreshBlocked = Boolean(
    bulk ||
      busy ||
      pendingInvite ||
      pendingReject ||
      pendingWhatsApp ||
      pendingDelete ||
      templateOpen ||
      waEdit,
  );
  const quietRefreshBlockedRef = useRef(quietRefreshBlocked);
  quietRefreshBlockedRef.current = quietRefreshBlocked;
  useEffect(() => {
    let hiddenAt = null;
    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        hiddenAt = Date.now();
        return;
      }
      // A flick between tabs is not worth a request; reading a profile is.
      const away = hiddenAt ? Date.now() - hiddenAt : 0;
      hiddenAt = null;
      if (away < 3000 || quietRefreshBlockedRef.current) return;
      load({ quiet: true });
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [load]);

  // Name click. On the web the link itself opens the profile in a new tab —
  // this only remembers who it was. In the phone app, where a new tab would
  // leave the app, it opens on this screen as it always did; the URL already
  // holds the view, so Back returns to it.
  const openCandidate = (row, e) => {
    setLastOpenedId(row.application_id);
    if (IS_NATIVE) {
      e?.preventDefault();
      onOpenCandidate?.(row);
    }
  };

  // Changing tab changes what a tick means — twelve people selected in "New
  // applicants" are not the twelve you want to move out of the rejected list.
  // Cleared rather than carried, and the same after a reload, where a selected
  // row may no longer exist or may no longer be on this tab.
  //
  // The screening filter counts as changing tab for this purpose: moving from
  // "Not screened" to "Screened" swaps the list out entirely. Every bulk action
  // already intersects the selection with pageRows, so a hidden row could never
  // have been acted on — but the banner would still have read "12 selected"
  // over a button about to touch three of them, which is a worse kind of wrong
  // than acting on the wrong rows: it is quietly acting on fewer.
  //
  // The WhatsApp filter and the sort order swap the list the same way.
  useEffect(() => {
    setSelected(new Set());
  }, [tab, loadSeq, page, screenFilter, waFilter, sortBy]);

  // Page 1 when the READER changes what they are looking at — a different round,
  // a different search, a different date range.
  //
  // Deliberately NOT on every reload. Rejecting one candidate on page 3 reloads
  // the list, and snapping back to page 1 each time would make working down a
  // long list by page impossible. Data shrinking underneath is handled by the
  // clamp on safePage instead, which lands you on the last real page rather than
  // on an empty one.
  //
  // Compared against the last values rather than fired on every run. The page
  // number can now come from the URL, and an effect that runs on mount — twice,
  // under StrictMode — would reset it to 1 before anybody saw it. Only a real
  // change of view resets the page.
  const viewKey = JSON.stringify([
    tab, search, perPage, dateField, range.from, range.to, sat, screenFilter, waFilter, sortBy,
  ]);
  const lastViewKey = useRef(viewKey);
  useEffect(() => {
    if (lastViewKey.current === viewKey) return;
    lastViewKey.current = viewKey;
    setPage(1);
  }, [viewKey]);

  // Back to All when the round changes. Carrying "Interviewed" onto a round
  // that has no interview would be a filter still switched on with nothing on
  // screen saying why the list looks short.
  //
  // On a real change only, for the same reason as the page reset above: the
  // filter restored from the URL must survive the mount.
  const lastTab = useRef(tab);
  useEffect(() => {
    if (lastTab.current === tab) return;
    lastTab.current = tab;
    setSat("ALL");
    setScreenFilter("ALL");
    setWaFilter(null);
  }, [tab]);

  // ── The URL follows the view ─────────────────────────────────────────────
  //
  // Replaced, not pushed: every chip click becoming a Back step would make Back
  // useless for the one thing it is for, which is leaving the page. Written
  // only when it differs, so this costs nothing on renders that change nothing.
  useEffect(() => {
    const next = new URLSearchParams(window.location.search);
    for (const k of URL_KEYS) next.delete(k);
    const put = (k, v, dflt) => {
      if (v != null && v !== "" && v !== dflt) next.set(k, String(v));
    };
    put("round", tab, null);
    put("q", search.trim(), "");
    put("cv", screenFilter, "ALL");
    put("sat", sat, "ALL");
    put("wa", waFilter, null);
    put("sort", sortBy, "score");
    put("pg", page, 1);
    put("per", perPage, 10);
    put("df", dateField, "applied");
    put("from", range.from, "");
    put("to", range.to, "");
    put("open", lastOpenedId, null);
    if (next.toString() === new URLSearchParams(window.location.search).toString()) return;
    setSearchParams(next, { replace: true });
  }, [
    tab, search, screenFilter, sat, waFilter, sortBy, page, perPage, dateField, range,
    lastOpenedId, setSearchParams,
  ]);

  // Collapse on the way out of a row you can no longer see. An expanded row
  // that scrolls off onto page 2 leaves the panel open when you come back to
  // it, which is confusing for something you opened four clicks ago.
  useEffect(() => {
    setExpandedId(null);
  }, [tab, page, perPage]);

  // ── Where a bulk move goes ────────────────────────────────────────────────
  //
  // Defaulted to the round after the one you are looking at, which is what
  // "move to next round" means from a tab. From "All" there is no such thing,
  // so it stays empty and the recruiter picks — guessing here would move people
  // somewhere nobody chose.
  const defaultNextStage = useMemo(() => {
    if (!active?.stageId) return "";
    const current = stages.find((s) => s.id === active.stageId);
    if (!current) return "";
    const later = stages
      .filter((s) => s.order > current.order)
      .sort((a, b) => a.order - b.order);
    return later[0]?.id ?? "";
  }, [active, stages]);

  useEffect(() => {
    setMoveTo(defaultNextStage);
  }, [defaultNextStage]);

  const moveTarget = stages.find((s) => s.id === Number(moveTo)) || null;

  // The current page, not the whole filtered set.
  //
  // "Select all" has to mean the rows under the tick you just clicked. A header
  // checkbox that silently selects 115 people across twelve pages, and then a
  // Reject button that acts on all of them, is the worst possible reading of
  // one click.
  const selectableRows = pageRows;
  const allChecked =
    selectableRows.length > 0 &&
    selectableRows.every((r) => selected.has(r.application_id));
  const someChecked = selectableRows.some((r) => selected.has(r.application_id));

  const toggleAll = () => {
    setSelected((prev) => {
      if (selectableRows.every((r) => prev.has(r.application_id))) return new Set();
      return new Set(selectableRows.map((r) => r.application_id));
    });
  };

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // ── Actions ───────────────────────────────────────────────────────────────

  const runScreening = async () => {
    setScreening(true);
    try {
      const res = await interviewService.runScreening(jobId, {});
      setToast({
        severity: res.data?.data?.failed ? "warning" : "success",
        message: res.data?.statusMessage || "Resumes read.",
      });
      await load();
    } catch (err) {
      setToast({ severity: "error", message: err.response?.data?.statusMessage || err.message });
    } finally {
      setScreening(false);
    }
  };

  // One server action seen from two places in the funnel: issuing the link moves
  // the stage and sends the email, so "move to AI screening" and "send link" are
  // the same call.
  const invite = async (row) => {
    // Last line of the pre-screen gate. The button is already disabled for
    // anybody held; this is for every other way of arriving here.
    if (heldForWhatsApp(row)) {
      setToast({
        severity: "info",
        message: `${row.candidate_name} has not agreed on WhatsApp yet. Mark them Interested, or skip WhatsApp for them, to invite.`,
      });
      return;
    }
    setBusy(row.application_id);
    try {
      const res = await interviewService.invite(row.application_id, {});
      const body = res.data;
      if (!body?.success) {
        setToast({ severity: "error", message: body?.statusMessage });
        return;
      }
      const email = body.data?.email;
      setToast({
        // A link that exists but was not emailed is a partial success. Reporting
        // it as a plain success is how a candidate ends up never invited while
        // the screen says they were.
        severity: email?.attempted && !email?.sent ? "warning" : "success",
        message: body.statusMessage,
        link: body.data?.interview_url,
      });
      await load();
    } catch (err) {
      setToast({ severity: "error", message: err.response?.data?.statusMessage || err.message });
    } finally {
      setBusy(null);
    }
  };

  // ── Moving somebody into a round ─────────────────────────────────────────
  //
  // There is no per-row move any more, and no `moveToRound` helper. The only
  // one this screen ever had was screening -> AI, and the server does not treat
  // that as a move: entering the AI round issues a signed link, spends
  // interview credit and emails the candidate. Because each of those fails for
  // a reason that names ONE person, `bulk-move-stage` refuses an AI_INTERVIEW
  // target outright —
  //
  //   400 "Moving someone into the AI screening round issues and emails their
  //        interview link, so it is done one candidate at a time. Use the
  //        invite action instead."
  //
  // — which is what the button used to return. It goes through `invite` now,
  // and the label says "Invite" rather than promising a move nothing can do.
  // Every other round is reached with the bulk control, which is `bulkAdvance`.

  // What the row's primary button does, by round type. Keeps the decision in
  // one place rather than in the Row component, which only knows a label.
  //
  // The screening button asks first. On the AI tab "Send link" says what it
  // does and the candidate is already in the round; from screening the same
  // click also emails a stranger, which is not a thing to discover afterwards.
  const primaryAction = (row) => {
    if (mode === "SCREENING") {
      if (!interviewStage) return undefined;
      // Checked before the dialog, not after it: asking "Invite Priya?" and
      // then refusing is worse than never asking.
      if (heldForWhatsApp(row)) return invite(row);
      return setPendingInvite({ row, stage: interviewStage });
    }
    if (mode === "AI_INTERVIEW") return invite(row);
    return undefined;
  };

  // ── Bulk advance ──────────────────────────────────────────────────────────
  //
  // A plain stage move for every target the server will move somebody to, and
  // an invite for the one it will not.
  //
  // Sending twenty invitations from a button labelled "Move" is the thing this
  // has to avoid, and it is avoided by naming it rather than by pretending the
  // AI round is an ordinary target: with that target selected the button reads
  // "Invite 20 to AI Screening", it confirms first, and it says what each one
  // costs. The AI round's own tab keeps its Send button (bulkSendLinks below)
  // for people who are already in the round.
  const bulkAdvance = async () => {
    const picked = pageRows.filter((r) => selected.has(r.application_id));
    const ids = picked.map((r) => r.application_id);
    if (!ids.length) return;

    // ── The rejected list restores; it does not advance ────────────────────
    //
    // The server refuses to move a rejected candidate into a round, because
    // un-rejecting somebody as a side effect of a bulk move is exactly the sort
    // of thing nobody notices happening to twelve people at once. So on this
    // tab the bulk action is the one that makes sense here: put them back.
    //
    // Worth having rather than leaving to the per-row undo arrow. The realistic
    // way to end up on this tab wanting twelve people back is a filter or a
    // sort that was not what somebody thought it was, and twelve individual
    // undos is enough friction that in practice it does not get done.
    if (mode === "REJECTED") {
      if (!onUnrejectCandidate) return;
      setBulk({ kind: "restore", done: 0, total: ids.length, label: "the pipeline" });
      const results = await mapWithLimit(picked, 3, async (row) => {
        try {
          await onUnrejectCandidate?.(row.application_id);
          return { ok: true, name: row.candidate_name };
        } catch (err) {
          return {
            ok: false,
            name: row.candidate_name,
            reason: err.response?.data?.statusMessage || err.message,
          };
        } finally {
          setBulk((b) => (b ? { ...b, done: b.done + 1 } : b));
        }
      });
      setBulk(null);
      setSelected(new Set());
      await load();
      onChanged?.();
      const failed = results.filter((r) => !r.ok);
      setToast({
        severity: failed.length ? "warning" : "success",
        message: failed.length
          ? `Restored ${results.length - failed.length} of ${results.length}. ` +
            `Could not restore: ${failed.map((f) => f.name).join(", ")}.`
          : `${results.length} back in the pipeline.`,
      });
      return;
    }

    if (!moveTarget) return;

    // The AI round is not a move. `bulk-move-stage` refuses an AI_INTERVIEW
    // target with a 400 — entering that round issues a link, spends credit and
    // emails the candidate, and each of those fails for a reason that names one
    // person, which one bulk response cannot report. Same act as the AI tab's
    // own Send button, so it goes down the same path: individual invites, three
    // at a time, and a toast that names who failed. Confirmed first, because
    // the button the operator pressed said "Move".
    if (moveTarget.type === "AI_INTERVIEW") {
      // The pre-screen gate, for a selection. The ones who have not agreed are
      // held back and NAMED — in the dialog, before anything is sent — and
      // the rest go ahead. Failing the whole batch because of one would make
      // somebody untick people by hand to get the others through.
      const allowed = picked.filter((r) => !heldForWhatsApp(r));
      const held = picked.filter((r) => heldForWhatsApp(r));
      if (!allowed.length) {
        setToast({
          severity: "info",
          message:
            `None of the ${picked.length} selected has agreed on WhatsApp yet. ` +
            "Mark them Interested, or skip WhatsApp for them, to invite.",
        });
        return;
      }
      setPendingInvite({
        ids: allowed.map((r) => r.application_id),
        count: allowed.length,
        stage: moveTarget,
        // Said before the run, not discovered as failures after it. The link is
        // delivered by email and nothing else, so these are already lost.
        noEmail: allowed.filter((r) => !r.email).length,
        held: held.map((r) => r.candidate_name),
      });
      return;
    }

    setBulk({ kind: "move", done: 0, total: ids.length, label: moveTarget.name });
    try {
      const res = await applicationService.bulkMoveStage(ids, moveTarget.id, "");
      const body = res.data;
      if (!body?.success) {
        setToast({ severity: "error", message: body?.statusMessage || "Could not move." });
        return;
      }
      setSelected(new Set());
      await load();
      onChanged?.();
      setToast({
        severity: body.data?.failed ? "warning" : "success",
        message: body.statusMessage,
      });
    } catch (err) {
      setToast({
        severity: "error",
        message: err.response?.data?.statusMessage || err.message,
      });
    } finally {
      setBulk(null);
    }
  };

  // ── Excel export ─────────────────────────────────────────────────────────
  //
  // One workbook: a Summary, everyone on one sheet, a sheet per round in
  // pipeline order, then Rejected. That is what "stage-wise and all" means in a
  // spreadsheet — a single flat sheet makes the reader filter it themselves
  // every time, and a file per round is five downloads with no view of the
  // whole pipeline.
  //
  // Built from the rows already in memory, so it costs no request.
  //
  // Uses `data.rows` — the UNPAGINATED, UNSEARCHED set — not `pageRows`.
  // Exporting page 2 of a text search is never what anybody means by "export".
  //
  // The date range IS reflected, because that one is applied server-side and
  // these rows are all that came back. `range` and not `from`/`to`: those two
  // are the input boxes, which can hold dates nobody has applied yet, and
  // labelling the file with a filter that was not used is worse than not
  // labelling it. The Summary sheet states the range and the filename says
  // "filtered", so a partial export cannot be mistaken for a complete one when
  // the file is opened next week.
  const exportExcel = () => {
    if (!data) return;
    const filtered = Boolean(range.from || range.to);
    try {
      const result = exportCandidatesWorkbook({
        job: data.job,
        rows: data.rows || [],
        stages: (data.stages || []).map((st) => ({ id: st.id, name: st.name })),
        filter: filtered ? { dateField, from: range.from, to: range.to } : undefined,
      });
      setToast({
        severity: "success",
        message:
          `${result.filename} downloaded — ${result.rows} candidate` +
          `${result.rows === 1 ? "" : "s"} across ${result.sheets} sheets.`,
      });
    } catch (err) {
      // Writing the file is synchronous and can fail where a browser blocks the
      // download, which would otherwise look like the button doing nothing.
      setToast({
        severity: "error",
        message: `Could not create the Excel file: ${err.message}`,
      });
    }
  };

  // ── Sending the link to a selection ──────────────────────────────────────
  //
  // This logic used to live inside bulkAdvance, fired when the MOVE target
  // happened to be the AI round — so "Move 20 to AI/Call Interview" emailed
  // twenty candidates. It is its own action now, on its own button, on the AI
  // round's tab.
  //
  // Individual invites, three at a time, rather than one bulk request: each one
  // costs a credit check, a signed link, a voice clip and an email, and each
  // can fail for a reason that names ONE person — no credits left, no email
  // address, the interview already sat. A single request could only report "6
  // failed", which sends somebody hunting through forty rows to find out who.
  //
  // `confirmedIds` is what the confirmation dialog showed. Passed in rather than
  // re-read from the selection here, so what is sent is exactly the list
  // somebody said yes to — not whatever is ticked by the time it runs. The AI
  // round's own button passes nothing and sends the current selection.
  const bulkSendLinks = async (confirmedIds) => {
    const ids = Array.isArray(confirmedIds)
      ? confirmedIds
      : pageRows.filter((r) => selected.has(r.application_id)).map((r) => r.application_id);
    if (!ids.length) return;

    setBulk({ kind: "send", done: 0, total: ids.length, label: "invited" });
    const results = await mapWithLimit(ids, 3, async (id) => {
      const row = rows.find((r) => r.application_id === id);
      // The pre-screen gate, once more, per person. Nobody held reaches here
      // through the dialog; this is the guarantee, not the filter.
      if (row && heldForWhatsApp(row)) {
        setBulk((b) => (b ? { ...b, done: b.done + 1 } : b));
        return { id, ok: false, name: row.candidate_name, reason: "not agreed on WhatsApp" };
      }
      try {
        const res = await interviewService.invite(id, {});
        const okBody = Boolean(res.data?.success);
        return {
          id,
          ok: okBody,
          name: row?.candidate_name,
          reason: okBody ? null : res.data?.statusMessage,
          // An invite that issued a link but could not email it is neither a
          // success nor a failure, and calling it either one is a lie the
          // candidate pays for.
          unmailed: okBody && res.data?.data?.email?.attempted && !res.data?.data?.email?.sent,
        };
      } catch (err) {
        return {
          id,
          ok: false,
          name: row?.candidate_name,
          reason: err.response?.data?.statusMessage || err.message,
        };
      } finally {
        setBulk((b) => (b ? { ...b, done: b.done + 1 } : b));
      }
    });

    setBulk(null);
    setSelected(new Set());
    await load();
    onChanged?.();

    const failed = results.filter((r) => !r.ok);
    const unmailed = results.filter((r) => r.unmailed);
    setToast({
      severity: failed.length || unmailed.length ? "warning" : "success",
      message: failed.length
        ? `Sent ${results.length - failed.length} of ${results.length}. ` +
          // Named, not counted. "3 failed" sends somebody hunting through forty
          // rows to find out who.
          `Could not send to: ${failed.map((f) => f.name || f.id).join(", ")}.`
        : unmailed.length
          ? `Links issued for ${results.length}, but the email did not go to ` +
            `${unmailed.map((f) => f.name || f.id).join(", ")} — copy their link and send it by hand.`
          : `Interview link sent to ${results.length}.`,
    });
  };

  // Runs whatever the confirmation is holding. One candidate and a selection are
  // the same server act — `POST /interview/invite/:id` — and differ only in how
  // the outcome is reported, so they share the dialog and split here.
  const runPendingInvite = () => {
    const p = pendingInvite;
    if (!p) return;
    setPendingInvite(null);
    return p.ids ? bulkSendLinks(p.ids) : invite(p.row);
  };

  // ── Reject the selection ──────────────────────────────────────────────────
  //
  // No confirmation dialog, deliberately. Rejecting is reversible — the status
  // goes back and the timeline keeps both events — so the safety net is an undo
  // that actually works rather than a prompt people learn to dismiss.
  //
  // The undo replays only the ids the SERVER says it changed. Replaying the
  // whole selection would also un-reject anyone who was already rejected before
  // the button was pressed, quietly putting people back in the running that
  // nobody asked to restore.
  // Collects the reason first -- see the note on `reject`. A bulk rejection is
  // the action most likely to be regretted and least likely to be remembered,
  // so it is the last one that should have been a bare click.
  const bulkRejectSelected = () => {
    const picked = pageRows.filter((r) => selected.has(r.application_id));
    const ids = picked.map((r) => r.application_id);
    if (!ids.length) return;
    setPendingReject({ ids, count: ids.length });
  };

  const confirmBulkReject = async (reason) => {
    const ids = pendingReject?.ids;
    if (!ids?.length) return;

    setBulk({ kind: "reject", done: 0, total: ids.length, label: "rejected" });
    try {
      const res = await applicationService.bulkReject(ids, reason);
      const body = res.data;
      if (!body?.success) {
        // Thrown rather than toasted: the dialog is still open and holds the
        // reason that was typed. Letting it surface there means the text is not
        // lost -- see RejectReasonDialog's own catch.
        throw new Error(body?.statusMessage || "Could not reject.");
      }
      setPendingReject(null);
      const reverted = body.data?.reverted || [];
      setSelected(new Set());
      await load();
      onChanged?.();
      setToast({
        severity: body.data?.failed ? "warning" : "info",
        message: body.statusMessage,
        undo: reverted.length
          ? async () => {
              setBulk({ kind: "restore", done: 0, total: reverted.length, label: "the pipeline" });
              await mapWithLimit(reverted, 3, async (id) => {
                try {
                  await onUnrejectCandidate?.(id);
                } catch {
                  // Reported in aggregate below rather than one toast per
                  // candidate — an undo that fires eleven red boxes is worse
                  // than the thing it is undoing.
                }
                setBulk((b) => (b ? { ...b, done: b.done + 1 } : b));
              });
              setBulk(null);
              await load();
              onChanged?.();
              setToast({
                severity: "success",
                message: `${reverted.length} back in the pipeline.`,
              });
            }
          : undefined,
      });
    } catch (err) {
      // Re-thrown, not toasted. The dialog is still open and holds the reason
      // the operator typed; surfacing the failure there keeps that text so it
      // does not have to be written again. See RejectReasonDialog.
      throw err;
    } finally {
      setBulk(null);
    }
  };

  // Opens the dialog. The rejection itself happens in confirmReject below,
  // once there is a reason to record.
  //
  // This used to reject immediately on click, sending no reason at all -- and
  // the server then stored the placeholder "Candidate rejected", which reads in
  // the audit trail exactly like something a person typed. The server now
  // refuses a rejection with no reason, so collecting one here is what keeps
  // the click from failing with an error the operator cannot act on.
  const reject = (row) => {
    if (!onRejectCandidate) return;
    setPendingReject({ row });
  };

  const confirmReject = async (reason) => {
    const row = pendingReject?.row;
    if (!row) return;
    setBusy(row.application_id);
    try {
      await onRejectCandidate(row.application_id, reason);
      setPendingReject(null);
      await load();
      // The rejected tab now shows the state, so this is a confirmation rather
      // than the only feedback — but it names WHO, which the list cannot do once
      // the row has moved to another tab, and it puts the way back one click away.
      setToast({
        severity: "info",
        message: `${row.candidate_name} rejected`,
        undo: () => unreject(row),
      });
    } catch (err) {
      // Re-thrown so the dialog reports it and keeps the reason. Toasting here
      // would close the dialog and lose what was typed.
      throw err;
    } finally {
      setBusy(null);
    }
  };

  const unreject = async (row) => {
    if (!onUnrejectCandidate) return;
    setBusy(row.application_id);
    try {
      await onUnrejectCandidate(row.application_id);
      await load();
      setToast({ severity: "success", message: `${row.candidate_name} is back in the pipeline` });
    } catch (err) {
      setToast({
        severity: "error",
        message: err.response?.data?.statusMessage || err.message,
      });
    } finally {
      setBusy(null);
    }
  };

  const doDelete = async (row) => {
    setPendingDelete(null);
    setBusy(row.application_id);
    try {
      const res = await applicationService.deleteApplication(row.application_id);
      await load();
      onChanged?.();
      setToast({
        severity: "success",
        message: res?.data?.data?.candidate_deleted
          ? `${row.candidate_name} deleted`
          : `${row.candidate_name} removed from this job — kept, they are in another job`,
      });
    } catch (err) {
      setToast({
        severity: "error",
        message: err.response?.data?.statusMessage || err.message,
      });
    } finally {
      setBusy(null);
    }
  };

  // ── Inline detail edits ───────────────────────────────────────────────────
  //
  // Optimistic, and it has to be: the cell reverts to reading mode the moment
  // the request resolves, and a full reload between those two moments makes the
  // value flicker from old to new while somebody is looking at it.
  //
  // On failure the previous value is put back AND the error is named. Silently
  // reverting would leave a recruiter certain they had recorded a 90-day notice
  // period that is not there.
  const saveDetail = async (row, patch) => {
    const before = { ...row };
    // Coerced to the shape the server will send back, not left as the raw
    // string from the input. Otherwise "0" in the notice box renders as "0 days"
    // for the length of the request and then snaps to "Immediate" — a value
    // changing in front of somebody who has just typed it reads as a bug even
    // when both readings mean the same thing.
    const optimistic = { ...patch };
    for (const k of ["notice_period_days", "expected_salary_annual"]) {
      if (k in optimistic && optimistic[k] != null) {
        const n = Number(String(optimistic[k]).replace(/[^0-9.]/g, ""));
        optimistic[k] = Number.isFinite(n) ? n : optimistic[k];
      }
    }
    setData((d) =>
      d
        ? {
            ...d,
            rows: d.rows.map((r) =>
              r.application_id === row.application_id ? { ...r, ...optimistic } : r,
            ),
          }
        : d,
    );
    try {
      const res = await interviewService.updateDetails(row.application_id, patch);
      if (!res.data?.success) throw new Error(res.data?.statusMessage || "Could not save.");
      // Replaced with what the server stored, not with what was typed. "12,00,000"
      // and "₹12L" both become a number there, and leaving the raw text on screen
      // would show something the database does not contain.
      const saved = res.data.data;
      setData((d) =>
        d
          ? {
              ...d,
              rows: d.rows.map((r) =>
                r.application_id === row.application_id
                  ? {
                      ...r,
                      // Every field this endpoint can normalise, not just the
                      // three it started with. The name and address are
                      // trimmed server-side, so leaving them on the optimistic
                      // value would show "  Abinash  " as saved while the
                      // database holds "Abinash".
                      candidate_name: saved.candidate_name ?? r.candidate_name,
                      email: saved.email ?? null,
                      phone: saved.phone ?? null,
                      experience: saved.experience,
                      notice_period_days: saved.notice_period_days,
                      expected_salary_annual: saved.expected_salary_annual,
                    }
                  : r,
              ),
            }
          : d,
      );
    } catch (err) {
      setData((d) =>
        d
          ? {
              ...d,
              rows: d.rows.map((r) =>
                r.application_id === row.application_id ? before : r,
              ),
            }
          : d,
      );
      setToast({
        severity: "error",
        message: err.response?.data?.statusMessage || err.message || "Could not save.",
      });
      throw err;
    }
  };

  // ── Sending the link by hand ──────────────────────────────────────────────
  //
  // Defined in ../utils/inviteMessage.js, not here, because WhatsApp and Copy
  // must send identical words and because the wording a stranger reads about a
  // job they applied for is worth being able to test without rendering a table.
  // That file also records which parts must never be hardcoded, and why.
  const inviteMessage = (row) =>
    buildInviteMessage(row, {
      jobTitle,
      durationMinutes: rubricState?.duration_minutes,
      jd: jobJd,
      signature: jobSignature,
    });

  // ── Copy ─────────────────────────────────────────────────────────────────
  //
  // Copies the WHOLE MESSAGE, not the bare URL.
  //
  // A bare link was never what anybody was trying to produce: every recruiter
  // who pressed this then typed the same four sentences around it by hand,
  // differently each time, and the candidate got no warning that the interview
  // is recorded and timed until after they had clicked. The email has said all
  // of that for a while. Paste now says it too.
  //
  // Shift-click still copies just the URL. Kept because pasting into a field
  // that wants a URL — a CRM, a form, a chat that linkifies — is a real thing,
  // and it would otherwise mean pasting the message and deleting around it.
  const copyLink = async (row, linkOnly = false) => {
    if (!row.interview_url) return;
    const payload = linkOnly ? row.interview_url : inviteMessage(row);
    try {
      await navigator.clipboard.writeText(payload);
      setToast({
        severity: "success",
        message: linkOnly
          ? `Interview link for ${row.candidate_name} copied`
          : `Invitation for ${row.candidate_name} copied — paste it into ` +
            `WhatsApp, SMS or email.` +
            (jobJd?.url ? " The job description link is included." : ""),
      });
    } catch {
      // Clipboard access is refused outside a secure context and in some
      // embedded browsers. Showing the link is the fallback that always works —
      // it can still be selected and copied by hand.
      //
      // Deliberately the LINK and not the message: this path exists because
      // copying failed, so the fallback has to be the part that cannot be
      // retyped. The wording around it can be.
      setToast({
        severity: "info",
        message: "Could not reach the clipboard. Here is the link:",
        link: row.interview_url,
        showLink: true,
      });
    }
  };

  // ── Opening a chat, by hand ──────────────────────────────────────────────
  //
  // No longer the primary route — `sendWhatsApp` below actually sends — but
  // kept, and offered inside the dialog. The gateway has been down before, and
  // when it is, opening the chat by hand is the difference between an
  // invitation that is late and one that never goes.
  const openWhatsApp = (row) => {
    const to = waNumber(row.phone);
    if (!to || !row.interview_url) return;
    window.open(
      `https://wa.me/${to}?text=${encodeURIComponent(inviteMessage(row))}`,
      "_blank",
      "noopener",
    );
  };

  // ── Sending it, rather than opening a chat ───────────────────────────────
  //
  // Same gateway the Travel ERP dashboard sends bookings on, reached through
  // our own backend so the store's token stays server-side. See
  // services/whatsappService.js for why not src/services/wpService.js.
  //
  // One request per candidate, three at a time, for the reason the bulk invite
  // does it that way: the message carries the candidate's own name and their
  // own signed link, so there is nothing to batch, and a number the gateway
  // rejects names ONE person. The toast names them.
  const sendWhatsApp = async (overrideMessage) => {
    // Captured up front. The dialog is closed before the toast is built, and
    // reading the state again after that would be reading it through a closure
    // that no longer says what the reader thinks it says.
    const asked = pendingWhatsApp?.rows || [];
    // A row with no link has nothing to send — inviteMessage would produce an
    // invitation with a blank where the interview goes.
    const targets = asked.filter((r) => r.interview_url && enotifyPhone(r.phone));
    if (!targets.length) return;

    const solo = targets.length === 1;
    setBulk({ kind: "whatsapp", done: 0, total: targets.length, label: "WhatsApp" });

    const results = await mapWithLimit(targets, 3, async (row) => {
      const res = await whatsappService.send({
        applicationId: row.application_id,
        // The single send may have been edited in the dialog. A bulk one is
        // built per candidate — each needs its own name and link.
        message: solo && overrideMessage ? overrideMessage : inviteMessage(row),
      });
      setBulk((b) => (b ? { ...b, done: b.done + 1 } : b));
      return { ...res, name: row.candidate_name };
    });

    setBulk(null);
    setPendingWhatsApp(null);
    if (!solo) setSelected(new Set());

    const failed = results.filter((r) => !r.ok);
    // The skipped are not failures — they were shown as unreachable in the
    // dialog before the send — but they are people who have not been contacted,
    // so the result cannot quietly count only the ones it tried.
    const skipped = asked.length - targets.length;

    setToast({
      severity: failed.length ? "warning" : "success",
      message: failed.length
        ? // Named, not counted, for the same reason as the bulk invite: "3
          // failed" sends somebody hunting through forty rows to find out who.
          `Sent to ${results.length - failed.length} of ${results.length}. ` +
          `Could not reach: ${failed.map((f) => `${f.name} (${f.reason})`).join("; ")}.`
        : solo
          ? `Invitation sent to ${results[0].name} on WhatsApp.`
          : `Invitation sent to ${results.length} on WhatsApp.` +
            (skipped ? ` ${skipped} skipped — no usable phone number.` : ""),
    });
  };

  // Opens the dialog. Nothing is sent until it is confirmed there.
  const askWhatsApp = (row) => setPendingWhatsApp({ rows: [row] });

  // ── The WhatsApp pre-screen ──────────────────────────────────────────────
  //
  // Everything below is separate from the invitation code above: different
  // message, different button, different server route. None of it can send
  // an interview link.

  // Where a candidate stands. Saved, then written into the row from what the
  // server stored. Thrown on failure so the popover keeps what was typed.
  const saveWhatsApp = async (row, patch) => {
    const res = await interviewService.updateWhatsApp(row.application_id, patch);
    if (!res.data?.success) throw new Error(res.data?.statusMessage || "Could not save.");
    const saved = res.data.data?.whatsapp || null;
    setData((d) =>
      d
        ? {
            ...d,
            rows: d.rows.map((r) =>
              r.application_id === row.application_id ? { ...r, whatsapp: saved } : r,
            ),
          }
        : d,
    );
    return saved;
  };

  // The note column. Only `note` is sent, so the WhatsApp status and call
  // time stay as they were. Thrown on failure so the cell keeps what was typed.
  const saveNote = async (row, note) => {
    try {
      await saveWhatsApp(row, { note });
    } catch (err) {
      setToast({
        severity: "error",
        message: `Could not save the note for ${row.candidate_name}. ${err.message || ""}`.trim(),
      });
      throw err;
    }
  };

  // HR sets aside a "Suggest reject" from a failed gate. The row takes the
  // verdict the server worked out from the score. Thrown on failure so the
  // popover keeps the reason that was typed.
  const dismissVerdict = async (row, reason) => {
    try {
      const res = await interviewService.dismissGate(row.application_id, reason);
      if (!res.data?.success) throw new Error(res.data?.statusMessage || "Could not dismiss.");
      const { verdict, verdict_reason } = res.data.data || {};
      setData((d) =>
        d
          ? {
              ...d,
              rows: d.rows.map((r) =>
                r.application_id === row.application_id
                  ? { ...r, interview_verdict: verdict, interview_verdict_reason: verdict_reason }
                  : r,
              ),
            }
          : d,
      );
      setToast({ severity: "success", message: `Suggestion dismissed for ${row.candidate_name}.` });
    } catch (err) {
      setToast({
        severity: "error",
        message: err.response?.data?.statusMessage || err.message || "Could not dismiss the suggestion.",
      });
      throw err;
    }
  };

  // One click: the candidate's chat, with this job's message typed in for
  // them. The recruiter presses Enter — nothing is sent from here.
  //
  // Marked "Messaged" as the chat opens, so they leave the "To contact" list.
  // Opening is not proof the message went, but it is what happens next in all
  // but the rarest case, and it is one click back if not.
  const prescreenChat = async (row) => {
    const to = waNumber(row.phone);
    if (!to) {
      setToast({
        severity: "warning",
        message: `${row.candidate_name}'s mobile number is missing or not usable. Correct it in the Phone column and try again.`,
      });
      return;
    }
    openPrescreenChat(to, buildPrescreenMessage(row, data?.job || {}), {
      native: IS_NATIVE,
      opener: chatOpener,
    });
    if (row.whatsapp?.status) return;
    try {
      await saveWhatsApp(row, { status: "MESSAGED" });
    } catch (err) {
      setToast({
        severity: "warning",
        message: `The chat opened, but ${row.candidate_name} could not be marked as messaged: ${
          err.response?.data?.statusMessage || err.message
        }`,
      });
    }
  };

  // The job's message. A message switches the pre-screen on; null switches it
  // off. Thrown on failure so the dialog keeps the text.
  const saveTemplate = async (template) => {
    const res = await interviewService.saveWhatsAppTemplate(jobId, template);
    if (!res.data?.success) throw new Error(res.data?.statusMessage || "Could not save.");
    const saved = res.data.data?.whatsapp_template || null;
    setData((d) => (d ? { ...d, job: { ...d.job, whatsapp_template: saved } } : d));
    setToast({ severity: "success", message: res.data.statusMessage });
  };

  const bulkWhatsApp = () => {
    const picked = pageRows.filter(
      (r) => selected.has(r.application_id) && r.interview_url,
    );
    if (!picked.length) {
      setToast({
        severity: "info",
        message:
          "None of the selected candidates has an interview link yet. Send the link first.",
      });
      return;
    }
    setPendingWhatsApp({ rows: picked });
  };

  // ── Render ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Box sx={{ display: "grid", placeItems: "center", py: 10 }}>
        <CircularProgress size={26} sx={{ color: C.line }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ borderRadius: 3 }} action={<Button onClick={load}>Retry</Button>}>
        {error}
      </Alert>
    );
  }

  const dateFilterOn = Boolean(range.from || range.to);
  // What the Filters button counts: the filters that live behind it. The CV
  // filter only counts while it is in there (the WhatsApp step is on); without
  // it, the CV filter is the chip row and shows its own state.
  const activeFilterCount =
    (dateFilterOn ? 1 : 0) + (waFilterOn && screenFilter !== "ALL" ? 1 : 0);

  // ── What a row and a card are given ──────────────────────────────────────
  //
  // One function for both views, so they can never be wired differently: the
  // same primary action, the same WhatsApp gate, the same reject and delete.
  // A second hand-written prop list is how one view ends up able to invite
  // somebody the other holds back.
  const candidateProps = (row, i) => ({
    row,
    mode,
    index: i,
    busy,
    checked: selected.has(row.application_id),
    onCheck: toggleOne,
    // Move, or send — decided by the round, in one place. This was `invite`
    // for every tab, which is why "Move to AI Interview" emailed people.
    onPrimary: primaryAction,
    // Both land on the one candidate page now. It used to be the
    // interview-only console, which had nothing to show for a candidate who
    // had not interviewed yet.
    onReview: (r) => navigate(`/ats/candidate/${r.application_id}`),
    onReject: reject,
    onUnreject: unreject,
    onDelete: setPendingDelete,
    onOpen: onOpenCandidate ? openCandidate : undefined,
    highlight: row.application_id === lastOpenedId,
    onActivate: activateRow,
    isNew: latestUpload.has(row.application_id),
    // The WhatsApp pre-screen: its chat button and status line on the
    // screening round, and the invite held until they agree.
    prescreen: mode === "SCREENING" && prescreenOn,
    held: mode === "SCREENING" && heldForWhatsApp(row),
    onPrescreenChat: prescreenChat,
    onEditWhatsApp: (anchor, r) => setWaEdit({ anchor, row: r }),
    onSaveNote: saveNote,
    onDismissVerdict: dismissVerdict,
    onCopyLink: copyLink,
    // Sends through the gateway now. The manual wa.me route is still one click
    // away, inside the dialog.
    onWhatsApp: askWhatsApp,
  });

  // Three different empty states, because they call for three different next
  // actions and "No candidates" tells you which of them you are in only by
  // accident.
  const emptyState = (
    <Box
      sx={{
        display: "grid",
        placeItems: "center",
        py: 9,
        bgcolor: C.card,
        border: `1px solid ${C.hair}`,
        borderRadius: 3,
      }}
    >
      <Typography sx={{ fontFamily: FONT, fontSize: 14, color: C.faint }}>
        {dateFilterOn
          ? "Nobody in that date range. Widen the dates or clear them."
          : search.trim()
            ? otherRoundTotal
              ? `Nobody matching “${search.trim()}” in this round — found in other rounds below.`
              : `Nobody matching “${search.trim()}”.`
            : waFilterKey !== "ALL" && tabRows.length
              ? "Nobody here with that WhatsApp status."
              : active?.key === "REJECTED"
                ? "Nobody has been rejected on this job."
                : data?.rows?.length
                  ? "Nobody at this stage."
                  : "No applications yet."}
      </Typography>
    </Box>
  );

  return (
    <Box>
      <style>{`
        @keyframes pipeIn { from { opacity:0; transform:translateY(4px) } to { opacity:1; transform:none } }
        @keyframes pipeBreathe { 0%,100% { opacity:1 } 50% { opacity:.35 } }
      `}</style>

      {/* ── Two lines, then the list ───────────────────────────────────────
          This used to be three crowded rows: a heading with five buttons, the
          rounds, then chips, a CV filter, a date range, a sort and the pager
          all on one line. Now:

            1  WHERE you are — the rounds — with search and the view switch.
            2  WHAT you are looking at — this round's chips — with the few
               actions that change it. The CV filter and the date range live
               behind one "Filters" button, which counts what is switched on.

          The pager moved under the list, where people look for it. The
          "Candidates · 21 applied" heading went: the page already says
          "Candidates 21" and every round tab carries its own count.

          Every control on these two lines is the same 32px height, which is
          most of what makes it read as one tidy bar. */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Stack
          direction="row"
          spacing={0.5}
          sx={{
            flex: 1,
            minWidth: 0,
            overflowX: "auto",
            "&::-webkit-scrollbar": { display: "none" },
            scrollbarWidth: "none",
            "& > *": { flexShrink: 0 },
          }}
        >
          {tabs.map((t) => {
            const on = t.key === active?.key;
            // While searching, each round shows how many it matched — where
            // the person you typed is, at a glance.
            const q = search.trim().toLowerCase();
            const n = q
              ? rowsForTab(t).filter((r) => matchesSearch(r, q)).length
              : rowsForTab(t).length;
            const hit = Boolean(q) && n > 0;
            return (
              <Box
                key={t.key}
                onClick={() => setTab(t.key)}
                sx={{
                  height: 32,
                  display: "flex",
                  alignItems: "center",
                  px: 1.25,
                  borderRadius: 2,
                  cursor: "pointer",
                  userSelect: "none",
                  fontFamily: FONT,
                  fontSize: 12.5,
                  fontWeight: 650,
                  whiteSpace: "nowrap",
                  transition: "all .16s",
                  color: on ? (t.rejected ? "#b91c1c" : C.ink) : C.muted,
                  bgcolor: on ? C.card : "transparent",
                  border: `1px solid ${on ? C.line : "transparent"}`,
                  boxShadow: on ? "0 1px 4px rgba(24,24,27,.05)" : "none",
                  "&:hover": { color: C.ink, bgcolor: on ? C.card : C.hair },
                }}
              >
                {t.label}
                <Box
                  component="span"
                  sx={
                    hit
                      ? {
                          ml: 0.75,
                          px: 0.6,
                          borderRadius: "8px",
                          fontSize: 11,
                          fontWeight: 800,
                          lineHeight: "16px",
                          color: C.accent,
                          bgcolor: C.accentSoft,
                        }
                      : { ml: 0.75, fontSize: 11.5, fontWeight: 700, color: on ? C.faint : C.line }
                  }
                >
                  {n}
                </Box>
              </Box>
            );
          })}
        </Stack>

        <TextField
          size="small"
          placeholder="Search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          inputProps={{ "aria-label": "Search name, email or phone" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 17, color: C.faint }} />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch("")} sx={{ p: 0.25 }} aria-label="Clear search">
                  <CloseIcon sx={{ fontSize: 14, color: C.faint }} />
                </IconButton>
              </InputAdornment>
            ) : null,
            sx: {
              height: 32,
              fontFamily: FONT,
              fontSize: 13,
              borderRadius: 2,
              bgcolor: C.card,
              "& fieldset": { borderColor: C.line },
            },
          }}
          sx={{ width: { xs: 150, md: 210 }, flexShrink: 0 }}
        />

        <Stack
          direction="row"
          sx={{
            flexShrink: 0,
            height: 32,
            border: `1px solid ${C.line}`,
            borderRadius: 2,
            overflow: "hidden",
            bgcolor: C.card,
          }}
        >
          {[
            { key: "list", label: "List view", Icon: ViewListRoundedIcon },
            { key: "cards", label: "Card view", Icon: ViewAgendaOutlinedIcon },
          ].map(({ key, label, Icon }) => (
            <IconButton
              key={key}
              size="small"
              aria-label={label}
              aria-pressed={view === key}
              onClick={() => setView(key)}
              sx={{
                borderRadius: 0,
                px: 0.9,
                bgcolor: view === key ? C.hair : "transparent",
                color: view === key ? C.ink : C.faint,
              }}
            >
              <Icon sx={{ fontSize: 17 }} />
            </IconButton>
          ))}
        </Stack>
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        sx={{ mb: 1, flexWrap: "wrap", columnGap: 1, rowGap: 1 }}
      >
        {/* This round's chips — the one question it is worked from. */}
        <Stack
          direction="row"
          alignItems="center"
          sx={{ flexWrap: "wrap", gap: 0.5, flex: "1 1 auto", minWidth: 0 }}
        >
          {waFilterOn ? (
            <>
              <WhatsAppIcon sx={{ fontSize: 16, color: "#25d366", mr: 0.5 }} />
              {WA_FILTERS.map((f) => (
                <Chip
                  key={f.key}
                  size="small"
                  label={`${f.label} ${waCounts[f.key] ?? 0}`}
                  onClick={() => pickChip(setWaFilter)(f.key)}
                  sx={filterChipSx(chipOn(waFilterKey, f.key))}
                />
              ))}
            </>
          ) : onScreeningRound ? (
            // Zero is shown, not hidden: "Not screened 0" answers "is there
            // anything left to run".
            SCREEN_FILTERS.map((f) => (
              <Chip
                key={f.key}
                size="small"
                label={`${f.label} ${screenCounts[f.key] ?? 0}`}
                onClick={() => pickChip(setScreenFilter)(f.key)}
                sx={filterChipSx(chipOn(screenFilter, f.key))}
              />
            ))
          ) : onInterviewRound ? (
            // "Awaiting 0" answers "is there anyone left to chase".
            SAT_FILTERS.map((f) => (
              <Chip
                key={f.key}
                size="small"
                label={`${f.label} ${satCounts[f.key] ?? 0}`}
                onClick={() => pickChip(setSat)(f.key)}
                sx={filterChipSx(chipOn(sat, f.key))}
              />
            ))
          ) : null}
        </Stack>

        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexShrink: 0 }}>
          {/* The job's WhatsApp pre-screen message. Only on the screening
              round, where it is sent from, and only once the server can
              store it. */}
          {onScreeningRound && data?.whatsapp_ready && (
            <Button
              startIcon={<WhatsAppIcon sx={{ fontSize: 15 }} />}
              onClick={() => setTemplateOpen(true)}
              sx={{
                ...toolBtnSx,
                color: "#128c7e",
                borderColor: prescreenOn ? "#86efac" : C.line,
                "&:hover": { borderColor: "#25d366", bgcolor: "#f0fdf4" },
              }}
            >
              {prescreenOn ? "Message" : "Set up WhatsApp"}
            </Button>
          )}

          {/* Only while there is something to read. A permanently greyed
              "All read" was a button that did nothing, taking room. */}
          {(counts.pending > 0 || screening) && (
            <Button
              startIcon={<AutoAwesomeIcon sx={{ fontSize: 15 }} />}
              onClick={runScreening}
              disabled={screening}
              sx={{ ...toolBtnSx, color: C.accent, "&:hover": { borderColor: C.accent, bgcolor: C.accentSoft } }}
            >
              {screening ? "Reading…" : `Read ${counts.pending}`}
            </Button>
          )}

          {/* The AI interview's questions and rubric, always reachable from the
              AI round. The only other way in is the warning banner, which is
              gone once the rubric has been reviewed — leaving no way back to
              the questions at all. */}
          {onInterviewRound && rubricState?.stage_id && (
            <Button
              startIcon={<QuizOutlinedIcon sx={{ fontSize: 16 }} />}
              onClick={() => navigate(`/ats/rubric/${rubricState.stage_id}`)}
              sx={toolBtnSx}
            >
              Interview questions
            </Button>
          )}

          {/* The CV filter and the date range, behind one button that says
              how many of them are on. */}
          <Button
            startIcon={<TuneRoundedIcon sx={{ fontSize: 16 }} />}
            onClick={(e) => setFiltersAnchor(e.currentTarget)}
            sx={{
              ...toolBtnSx,
              ...(activeFilterCount
                ? { color: C.accent, borderColor: C.accent, bgcolor: C.accentSoft }
                : {}),
            }}
          >
            Filters{activeFilterCount ? ` · ${activeFilterCount}` : ""}
          </Button>

          {/* The order, where the resume rating is the order. The AI round
              sorts by interview score and the rejected list by date. */}
          {sortable && (
            <TextField
              select
              size="small"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              variant="standard"
              InputProps={{ disableUnderline: true }}
              SelectProps={{
                sx: { fontFamily: FONT, fontSize: 12.5, fontWeight: 650 },
                renderValue: (v) => (v === "newest" ? "Newest first" : "Top score"),
              }}
              sx={compactSelectSx(sortBy !== "score")}
            >
              <MenuItem value="score" sx={{ fontFamily: FONT, fontSize: 12.5 }}>
                Resume score, highest first
              </MenuItem>
              <MenuItem value="newest" sx={{ fontFamily: FONT, fontSize: 12.5 }}>
                Newest upload first
              </MenuItem>
            </TextField>
          )}

          {/* Every candidate on this job, round by round, in one workbook.
              An icon: used weekly rather than hourly. */}
          <Tooltip title="Export to Excel">
            <span>
              <IconButton
                onClick={exportExcel}
                disabled={!(data?.rows || []).length}
                aria-label="Export to Excel"
                sx={{ ...toolBtnSx, px: 0, width: 32, minWidth: 32 }}
              >
                <FileDownloadOutlinedIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {/* ── Filters ──────────────────────────────────────────────────────── */}
      <Popover
        open={Boolean(filtersAnchor)}
        anchorEl={filtersAnchor}
        onClose={() => setFiltersAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: { mt: 0.75, p: 2, width: 300, borderRadius: 2.5, border: `1px solid ${C.line}` } }}
      >
        {/* Whose CV has been read — here while the WhatsApp chips hold the
            chip slot. Same filter, same counts, same URL key. */}
        {waFilterOn && (
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontFamily: FONT, fontSize: 11, fontWeight: 750, letterSpacing: 0.6, color: C.faint, mb: 0.75 }}>
              RESUME
            </Typography>
            <Stack direction="row" sx={{ flexWrap: "wrap", gap: 0.5 }}>
              {SCREEN_FILTERS.map((f) => (
                <Chip
                  key={f.key}
                  size="small"
                  label={`${f.label} ${screenCounts[f.key] ?? 0}`}
                  onClick={() => pickChip(setScreenFilter)(f.key)}
                  sx={filterChipSx(chipOn(screenFilter, f.key))}
                />
              ))}
            </Stack>
          </Box>
        )}

        {/* Which date, then the range. The field comes first because the
            range means nothing without it: "between the 1st and the 7th" is
            three different lists depending on whether that is when they
            applied, when we read their CV, or when we turned them down. */}
        <Typography sx={{ fontFamily: FONT, fontSize: 11, fontWeight: 750, letterSpacing: 0.6, color: C.faint, mb: 0.75 }}>
          DATE
        </Typography>
        <TextField
          select
          fullWidth
          size="small"
          value={dateField}
          onChange={(e) => setDateField(e.target.value)}
          SelectProps={{ sx: { fontFamily: FONT, fontSize: 13 } }}
        >
          {DATE_FIELDS.map((f) => (
            <MenuItem key={f.key} value={f.key} sx={{ fontFamily: FONT, fontSize: 13 }}>
              {f.label}
            </MenuItem>
          ))}
        </TextField>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
          <TextField
            size="small"
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            inputProps={{ max: to || undefined, "aria-label": "From date", style: { fontFamily: FONT, fontSize: 12.5 } }}
            sx={{ flex: 1 }}
          />
          <Typography sx={{ fontFamily: FONT, fontSize: 12, color: C.faint }}>to</Typography>
          <TextField
            size="small"
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            // Stops the impossible range being enterable rather than reporting
            // it afterwards as an empty list, which reads as "nobody applied".
            inputProps={{ min: from || undefined, "aria-label": "To date", style: { fontFamily: FONT, fontSize: 12.5 } }}
            sx={{ flex: 1 }}
          />
        </Stack>

        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2 }}>
          <Button
            size="small"
            disabled={!activeFilterCount && !from && !to}
            onClick={() => {
              setFrom("");
              setTo("");
              // Only when it is in here. Without the WhatsApp step the CV
              // filter is the chip row outside, and is not this button's.
              if (waFilterOn) setScreenFilter("ALL");
            }}
            sx={{ fontFamily: FONT, textTransform: "none", fontWeight: 650, color: C.muted }}
          >
            Clear all
          </Button>
          <Button
            size="small"
            variant="contained"
            disableElevation
            onClick={() => setFiltersAnchor(null)}
            sx={{ fontFamily: FONT, textTransform: "none", fontWeight: 700, bgcolor: C.ink, "&:hover": { bgcolor: "#27272a" } }}
          >
            Done
          </Button>
        </Stack>
      </Popover>

      {screening && (
        <LinearProgress
          sx={{
            mb: 1,
            height: 2,
            borderRadius: 2,
            bgcolor: C.hair,
            "& .MuiLinearProgress-bar": { bgcolor: C.accent },
          }}
        />
      )}

      {/* ── Bulk bar ──────────────────────────────────────────────────────
          Appears only with a selection. A permanently visible bar with a
          disabled button is a control you learn to ignore. */}
      <Collapse in={selected.size > 0}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1}
          alignItems={{ md: "center" }}
          sx={{
            mb: 1.25,
            p: 1.25,
            borderRadius: 2,
            bgcolor: C.accentSoft,
            border: "1px solid #c7d2fe",
          }}
        >
          <Typography
            sx={{ fontFamily: FONT, fontSize: 13, fontWeight: 700, color: "#3730a3" }}
          >
            {selected.size} selected
          </Typography>

          {/* No round picker on the rejected list. The server refuses to move a
              rejected candidate into a round, so offering the choice would be
              offering an action that cannot succeed. */}
          {mode !== "REJECTED" && (
            <TextField
              select
              size="small"
              value={moveTo}
              onChange={(e) => setMoveTo(e.target.value)}
              SelectProps={{
                displayEmpty: true,
                sx: { fontFamily: FONT, fontSize: 12.5, borderRadius: 2, bgcolor: C.card },
              }}
              sx={{ minWidth: 210, "& .MuiSelect-select": { py: 0.85 } }}
            >
              <MenuItem value="" disabled sx={{ fontFamily: FONT, fontSize: 12.5 }}>
                Choose a round…
              </MenuItem>
              {stages.map((s) => (
                <MenuItem
                  key={s.id}
                  value={s.id}
                  // Moving people into the round they are already in is the one
                  // choice that does nothing, so it is not offered.
                  disabled={s.id === active?.stageId}
                  sx={{ fontFamily: FONT, fontSize: 12.5 }}
                >
                  {s.name}
                </MenuItem>
              ))}
            </TextField>
          )}

          <Button
            variant="contained"
            disableElevation
            disabled={(mode !== "REJECTED" && !moveTarget) || Boolean(bulk)}
            onClick={bulkAdvance}
            startIcon={
              mode === "REJECTED" ? (
                <UndoRoundedIcon sx={{ fontSize: 16 }} />
              ) : moveTarget?.type === "AI_INTERVIEW" ? (
                <SendOutlinedIcon sx={{ fontSize: 16 }} />
              ) : (
                <ArrowForwardIcon sx={{ fontSize: 16 }} />
              )
            }
            sx={{
              fontFamily: FONT,
              textTransform: "none",
              fontWeight: 700,
              fontSize: 13,
              borderRadius: 2,
              px: 1.75,
              whiteSpace: "nowrap",
              bgcolor: C.accent,
              "&:hover": { bgcolor: "#4f46e5" },
            }}
          >
            {/* Only while THIS action is the one running. It used to fire on
                any bulk run, so sending links made this button claim to be
                moving people. */}
            {bulk && (bulk.kind === "move" || bulk.kind === "restore")
              ? `${bulk.kind === "restore" ? "Restoring" : "Moving"} ${bulk.done}/${bulk.total}…`
              : // The AI tab has its own Send button that reports this. On any
                // other tab THIS button started the invites, so this is the one
                // that has to show them running.
                bulk?.kind === "send" && mode !== "AI_INTERVIEW"
                ? `Inviting ${bulk.done}/${bulk.total}…`
                : mode === "REJECTED"
                  ? `Put ${selected.size} back`
                  : moveTarget
                    ? `${moveTarget.type === "AI_INTERVIEW" ? "Invite" : "Move"} ` +
                      `${selected.size} to ${moveTarget.name}`
                    : `Move ${selected.size}`}
          </Button>

          {/* Sending is its own act, so it is its own button — and only on the
              round that issues links. It used to be what "Move to AI/Call
              Interview" secretly did. */}
          {mode === "AI_INTERVIEW" && (
            <Button
              variant="outlined"
              disabled={Boolean(bulk)}
              // Wrapped: handed over bare, the click event would arrive as
              // the confirmed-ids argument.
              onClick={() => bulkSendLinks()}
              startIcon={<SendOutlinedIcon sx={{ fontSize: 16 }} />}
              sx={{
                fontFamily: FONT,
                textTransform: "none",
                fontWeight: 650,
                fontSize: 13,
                borderRadius: 2,
                px: 1.75,
                whiteSpace: "nowrap",
                borderColor: C.line,
                color: C.ink,
                "&:hover": { borderColor: C.accent, color: C.accent },
              }}
            >
              {bulk?.kind === "send"
                ? `Sending ${bulk.done}/${bulk.total}…`
                : `Send link to ${selected.size}`}
            </Button>
          )}

          {/* The same invitation, down the other pipe. Recruiters were copying
              it and pasting it into WhatsApp one candidate at a time; this is
              that, through the gateway, for the whole selection. Only on the AI
              round, because only there does everybody have a link to send. */}
          {mode === "AI_INTERVIEW" && (
            <Button
              variant="outlined"
              disabled={Boolean(bulk)}
              onClick={bulkWhatsApp}
              startIcon={<WhatsAppIcon sx={{ fontSize: 16 }} />}
              sx={{
                fontFamily: FONT,
                textTransform: "none",
                fontWeight: 650,
                fontSize: 13,
                borderRadius: 2,
                px: 1.75,
                whiteSpace: "nowrap",
                borderColor: C.line,
                color: "#128c7e",
                "&:hover": { borderColor: "#25d366", bgcolor: "#f0fdf4" },
              }}
            >
              {bulk?.kind === "whatsapp"
                ? `WhatsApp ${bulk.done}/${bulk.total}…`
                : `WhatsApp ${selected.size}`}
            </Button>
          )}

          {/* The other half of working down a list with checkboxes. Outlined
              rather than filled: it is the destructive one of the two, and
              giving it the same weight as "move" is how somebody advancing a
              shortlist rejects it instead. */}
          {mode !== "REJECTED" && (
            <Button
              variant="outlined"
              disabled={Boolean(bulk)}
              onClick={bulkRejectSelected}
              startIcon={<BlockOutlinedIcon sx={{ fontSize: 16 }} />}
              sx={{
                fontFamily: FONT,
                textTransform: "none",
                fontWeight: 650,
                fontSize: 13,
                borderRadius: 2,
                px: 1.75,
                whiteSpace: "nowrap",
                color: "#b91c1c",
                borderColor: "#fecaca",
                bgcolor: C.card,
                "&:hover": { borderColor: "#b91c1c", bgcolor: "#fef2f2" },
              }}
            >
              Reject {selected.size}
            </Button>
          )}

          <Button
            onClick={() => setSelected(new Set())}
            sx={{
              fontFamily: FONT,
              textTransform: "none",
              fontWeight: 650,
              fontSize: 12.5,
              color: C.muted,
            }}
          >
            Clear
          </Button>

          {mode !== "REJECTED" && moveTarget?.type === "AI_INTERVIEW" && (
            <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: "#4338ca" }}>
              Each one issues a link, spends a credit and emails the candidate.
            </Typography>
          )}
        </Stack>
      </Collapse>

      {bulk && (
        <LinearProgress
          variant="determinate"
          value={(bulk.done / Math.max(1, bulk.total)) * 100}
          sx={{
            mb: 1.25,
            height: 3,
            borderRadius: 2,
            bgcolor: C.hair,
            "& .MuiLinearProgress-bar": { bgcolor: C.accent },
          }}
        />
      )}

      {/* ── Is this round able to interview anybody? ──────────────────────
          Two failures wear the same face on a row: "Send link" that errors
          because there is no rubric, and "Send link" that works but scores the
          candidate against generic anchors nobody read. The second is the worse
          one — it produces numbers that look exactly as authoritative as real
          ones — so both are stated here rather than discovered one candidate at
          a time. */}
      {rubricState && !rubricState.exists && (
        <Alert
          severity="error"
          sx={{ mb: 1.5, borderRadius: 2, fontFamily: FONT, fontSize: 13.5 }}
          action={
            <Button
              size="small"
              onClick={() => navigate(`/ats/rubric/${rubricState.stage_id}`)}
              sx={{ textTransform: "none", fontWeight: 700, whiteSpace: "nowrap" }}
            >
              Set it up
            </Button>
          }
        >
          <strong>{rubricState.stage_name}</strong> has no rubric, so nobody here
          can be sent an interview link — there is nothing to ask them or score
          them against.
        </Alert>
      )}

      {rubricState?.exists && rubricState.needs_review && (
        <Alert
          severity="warning"
          sx={{ mb: 1.5, borderRadius: 2, fontFamily: FONT, fontSize: 13.5 }}
          action={
            <Button
              size="small"
              onClick={() => navigate(`/ats/rubric/${rubricState.stage_id}`)}
              sx={{ textTransform: "none", fontWeight: 700, whiteSpace: "nowrap" }}
            >
              Review it
            </Button>
          }
        >
          <strong>{rubricState.stage_name}</strong> is using the generic starter
          rubric that was created with the job. Interviews will run and be
          scored, but against anchors written for “an operational role” rather
          than this one.
        </Alert>
      )}

      {!interviewStage && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 3, fontFamily: FONT, fontSize: 13.5 }}>
          This job has no <strong>HR/AI Screening</strong> round, so nobody here can
          be sent an interview link. Edit the job and set its first round to
          HR/AI Screening — new jobs get one automatically.
        </Alert>
      )}

      {/* ── Cards ────────────────────────────────────────────────────────
          The same page of the same list, read one candidate at a time. Same
          selection, same bulk bar, same handlers — see candidateProps. */}
      {view === "cards" ? (
        <Box>
          {pageRows.length > 0 && (
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1, pl: 0.5 }}>
              <Checkbox
                size="small"
                checked={allChecked}
                indeterminate={!allChecked && someChecked}
                onChange={toggleAll}
                sx={{ p: 0.25, color: C.line, "&.Mui-checked": { color: C.accent } }}
              />
              <Typography sx={{ fontFamily: FONT, fontSize: 12, color: C.muted }}>
                {allChecked ? "Clear selection" : "Select everyone shown"}
              </Typography>
            </Stack>
          )}
          {pageRows.length === 0 ? (
            emptyState
          ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", xl: "1fr 1fr" },
                gap: 1,
              }}
            >
              {pageRows.map((row, i) => (
                <CandidateCard key={row.application_id} {...candidateProps(row, i)} />
              ))}
            </Box>
          )}
        </Box>
      ) : (
      /* The table scrolls sideways rather than crushing eleven columns into a
          phone. A squashed table is not a compact table. */
      <Box sx={{ overflowX: "auto", pb: 1, mx: -0.5, px: 0.5 }}>
        <Box sx={{ minWidth: TABLE_MIN }}>
          <TableHead
            selectable={rows.length > 0}
            allChecked={allChecked}
            someChecked={someChecked}
            onToggleAll={toggleAll}
          />

          {pageRows.length === 0
            ? emptyState
            : pageRows.map((row, i) => (
                <Row
                  key={row.application_id}
                  {...candidateProps(row, i)}
                  selectable
                  onSaveDetail={saveDetail}
                  open={expandedId === row.application_id}
                  onToggle={toggleExpanded}
                />
              ))}
        </Box>
      </Box>
      )}

      {/* ── How many, and which page ─────────────────────────────────────
          Under the list, where people look for it once they reach the end of
          a page. 1-indexed for the reader, and honest about a short last
          page: firstIndex + pageRows.length, not safePage * perPage. */}
      {rows.length > 0 && (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="flex-end"
          sx={{ mt: 1.25 }}
        >
          <Typography sx={{ fontFamily: FONT, fontSize: 12, color: C.muted }}>Per page</Typography>
          <TextField
            select
            size="small"
            value={perPage}
            onChange={(e) => setPerPage(Number(e.target.value))}
            variant="standard"
            InputProps={{ disableUnderline: true }}
            SelectProps={{ sx: { fontFamily: FONT, fontSize: 12.5, fontWeight: 700 } }}
            sx={{ ...compactSelectSx(false), width: 64 }}
          >
            {PER_PAGE_OPTIONS.map((n) => (
              <MenuItem key={n} value={n} sx={{ fontFamily: FONT, fontSize: 12.5 }}>
                {n}
              </MenuItem>
            ))}
          </TextField>
          <Typography sx={{ fontFamily: FONT, fontSize: 12, color: C.muted, whiteSpace: "nowrap", px: 0.5 }}>
            {firstIndex + 1}–{firstIndex + pageRows.length} of {rows.length}
          </Typography>
          <IconButton
            size="small"
            disabled={safePage <= 1}
            aria-label="Previous page"
            // safePage, not the stored page. If the list shrank while you were
            // on page 5 of 2, stepping from the stale 5 would set 4 and change
            // nothing on screen — the button would look broken.
            onClick={() => setPage(Math.max(1, safePage - 1))}
            sx={{ border: `1px solid ${C.line}`, borderRadius: "6px", p: 0.4 }}
          >
            <ChevronLeftRoundedIcon sx={{ fontSize: 17 }} />
          </IconButton>
          <IconButton
            size="small"
            disabled={safePage >= pageCount}
            aria-label="Next page"
            onClick={() => setPage(Math.min(pageCount, safePage + 1))}
            sx={{ border: `1px solid ${C.line}`, borderRadius: "6px", p: 0.4 }}
          >
            <ChevronRightRoundedIcon sx={{ fontSize: 17 }} />
          </IconButton>
        </Stack>
      )}

      {/* ── The same search, in other rounds ────────────────────────────────
          Read-only on purpose: each line only takes you to the candidate's
          round, where their row has the right buttons. */}
      {otherRoundTotal > 0 && (
        <Box
          sx={{
            mt: 2,
            border: `1px solid ${C.hair}`,
            borderRadius: "10px",
            bgcolor: C.wash,
            p: 1.5,
          }}
        >
          <Typography sx={{ fontFamily: FONT, fontSize: 12.5, fontWeight: 700, color: C.ink, mb: 1 }}>
            Also found in other rounds
            <Box component="span" sx={{ ml: 0.75, color: C.faint, fontWeight: 600 }}>
              {otherRoundTotal}
            </Box>
          </Typography>
          <Stack spacing={0.5}>
            {otherRoundMatches.flatMap((g) =>
              g.rows.map((r) => (
                <Stack
                  key={`${g.tab.key}-${r.application_id}`}
                  direction="row"
                  alignItems="center"
                  spacing={1.5}
                  onClick={() => openInRound(g.tab, r)}
                  sx={{
                    px: 1.25,
                    py: 0.75,
                    bgcolor: C.card,
                    border: `1px solid ${C.hair}`,
                    borderRadius: "8px",
                    cursor: "pointer",
                    minWidth: 0,
                    "&:hover": { borderColor: C.line },
                    "&:hover .other-round-go": { color: C.accent },
                  }}
                >
                  <Typography
                    noWrap
                    sx={{
                      fontFamily: FONT,
                      fontSize: 13,
                      fontWeight: 650,
                      color: g.tab.rejected ? C.muted : C.ink,
                      textDecoration: g.tab.rejected ? "line-through" : "none",
                      flexShrink: 0,
                      maxWidth: 220,
                    }}
                  >
                    {r.candidate_name}
                  </Typography>
                  <Meta sx={{ flex: 1, minWidth: 0 }}>
                    {[r.email, r.phone].filter(Boolean).join("  ·  ")}
                  </Meta>
                  <Box
                    component="span"
                    sx={{
                      flexShrink: 0,
                      px: 0.9,
                      borderRadius: "6px",
                      fontFamily: FONT,
                      fontSize: 11,
                      fontWeight: 700,
                      lineHeight: "20px",
                      color: g.tab.rejected ? "#b91c1c" : C.body,
                      bgcolor: g.tab.rejected ? "#fef2f2" : C.wash,
                      border: `1px solid ${C.hair}`,
                    }}
                  >
                    {g.tab.label}
                  </Box>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={0.4}
                    className="other-round-go"
                    sx={{ flexShrink: 0, color: C.muted, transition: "color .15s" }}
                  >
                    <Typography sx={{ fontFamily: FONT, fontSize: 12, fontWeight: 650, color: "inherit" }}>
                      Open in round
                    </Typography>
                    <ArrowForwardIcon sx={{ fontSize: 15 }} />
                  </Stack>
                </Stack>
              )),
            )}
          </Stack>
        </Box>
      )}

      <Typography
        sx={{ fontFamily: FONT, fontSize: 12.5, lineHeight: 1.8, color: C.faint, mt: 3, maxWidth: 820 }}
      >
        {active?.key === "REJECTED"
          ? "Everyone turned down on this job, most recent first. Nothing here is final — the undo arrow on a row puts somebody back in the pipeline, and the rejection stays in their history either way."
          : active?.unplaced
            ? "These candidates are not on any of this job's rounds — the round they were on no longer exists, or the job had none when they applied. Use Move to put them on one."
          : active?.stageType === "AI_INTERVIEW"
            ? "Sorted by interview score — recorded answers marked against anchors your team wrote. Open a row for the evidence behind every number. Experience, notice and expected salary are yours to edit; click a cell."
            : "Sorted by the resume rating out of 10. It rates the document, not the person: a strong candidate with a thin CV lands low, and “Unclear from CV” means the resume did not say — not that they are weak. Experience, notice and expected salary are yours to edit; click a cell."}
      </Typography>

      {/* Why we said no. Rejecting used to be a bare click that sent no reason
          at all; the server now refuses one, and this collects it. Doubles as
          the confirmation the button never had — reject sits one icon from
          delete on a dense row, and the bulk path acted on up to 200 people
          immediately. */}
      <RejectReasonDialog
        open={Boolean(pendingReject)}
        onClose={() => setPendingReject(null)}
        candidateName={pendingReject?.row?.candidate_name}
        count={pendingReject?.count || 1}
        busy={
          pendingReject?.ids
            ? Boolean(bulk)
            : busy === pendingReject?.row?.application_id
        }
        onConfirm={
          pendingReject?.ids ? confirmBulkReject : confirmReject
        }
      />

      {/* Entering the AI round is not a quiet move — the server issues a signed
          link, checks credit and emails the candidate, and offers no way to do
          the first without the last. The button that opens this says "Invite"
          for that reason; this states the whole of it before a stranger is
          written to, which is the part that cannot be undone. */}
      <Dialog
        open={Boolean(pendingInvite)}
        onClose={() => setPendingInvite(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogContent sx={{ p: 3 }}>
          <Typography sx={{ fontSize: 17, fontWeight: 700, color: C.ink }}>
            {pendingInvite?.ids
              ? `Invite ${pendingInvite.count} to ${pendingInvite?.stage?.name}?`
              : `Invite ${pendingInvite?.row?.candidate_name}?`}
          </Typography>
          <Typography sx={{ fontSize: 13.5, color: C.muted, mt: 1, lineHeight: 1.6 }}>
            {pendingInvite?.ids
              ? `Each one moves to ${pendingInvite?.stage?.name}, is issued an interview link and is emailed it. One interview credit is checked per candidate.`
              : `This moves them to ${pendingInvite?.stage?.name}, issues an interview link and emails it to `}
            {!pendingInvite?.ids && (
              <Box component="span" sx={{ color: C.ink, fontWeight: 650 }}>
                {pendingInvite?.row?.email}
              </Box>
            )}
            {!pendingInvite?.ids && "."}
          </Typography>
          {pendingInvite?.ids ? (
            <Typography sx={{ fontSize: 12.5, color: C.faint, mt: 1.25, lineHeight: 1.55 }}>
              They are sent one at a time, three at once, and the result names
              anyone it could not reach.
              {pendingInvite?.noEmail
                ? ` ${pendingInvite.noEmail} of them ${
                    pendingInvite.noEmail === 1 ? "has" : "have"
                  } no email address on file and will fail.`
                : ""}
            </Typography>
          ) : (
            <Typography sx={{ fontSize: 12.5, color: C.faint, mt: 1.25, lineHeight: 1.55 }}>
              There is no way to put somebody in this round without inviting
              them — the move and the email are one action on the server.
            </Typography>
          )}
          {/* Who the WhatsApp pre-screen held back, by name, before the send —
              so "Invite 3" under a selection of five is never a mystery. */}
          {pendingInvite?.held?.length ? (
            <Alert severity="info" sx={{ mt: 1.25, borderRadius: 2, fontSize: 12.5 }}>
              Not included — {pendingInvite.held.length === 1 ? "has" : "have"} not agreed on
              WhatsApp yet: {pendingInvite.held.join(", ")}.
            </Alert>
          ) : null}
          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2.5 }}>
            <Button
              onClick={() => setPendingInvite(null)}
              sx={{ textTransform: "none", fontWeight: 650, color: C.muted }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              disableElevation
              startIcon={<SendOutlinedIcon sx={{ fontSize: 16 }} />}
              onClick={runPendingInvite}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                bgcolor: C.accent,
                "&:hover": { bgcolor: "#4f46e5" },
              }}
            >
              {pendingInvite?.ids ? `Invite ${pendingInvite.count}` : "Invite"}
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>

      {/* The invitation, sent rather than pasted. Shows the resolved number and
          the exact words before anything leaves, because a WhatsApp message to
          a stranger cannot be recalled and the number came off a spreadsheet
          import nobody has read. */}
      <WhatsAppInviteDialog
        open={Boolean(pendingWhatsApp)}
        onClose={() => setPendingWhatsApp(null)}
        rows={pendingWhatsApp?.rows || []}
        buildMessage={inviteMessage}
        progress={bulk?.kind === "whatsapp" ? bulk : null}
        onSend={sendWhatsApp}
        onOpenWhatsApp={openWhatsApp}
      />

      {/* The job's WhatsApp pre-screen message — saving one switches the step
          on, removing it switches it off. Previewed against the first
          candidate in view, so {name} is seen as a candidate will read it. */}
      <WhatsAppTemplateDialog
        open={templateOpen}
        onClose={() => setTemplateOpen(false)}
        job={data?.job}
        sampleRow={pageRows[0] || (data?.rows || [])[0] || null}
        onSave={saveTemplate}
        // Where chats open on this computer. A set-once choice, so it lives
        // here rather than on the filter bar. Not offered in the phone app,
        // which always hands over to the WhatsApp app.
        opener={IS_NATIVE ? null : chatOpener}
        onOpenerChange={chooseChatOpener}
      />

      {/* One candidate's WhatsApp status, call time and note. */}
      <WhatsAppStatusPopover
        anchorEl={waEdit?.anchor || null}
        row={waEdit?.row || null}
        onClose={() => setWaEdit(null)}
        onSave={saveWhatsApp}
      />

      {/* Named, and honest about the two possible outcomes. A generic "are you
          sure" would be wrong here — whether the person is deleted or just taken
          off this job depends on something the reader cannot see from this row. */}
      <Dialog
        open={Boolean(pendingDelete)}
        onClose={() => setPendingDelete(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogContent sx={{ p: 3 }}>
          <Typography sx={{ fontSize: 17, fontWeight: 700, color: C.ink }}>
            Delete {pendingDelete?.candidate_name}?
          </Typography>
          <Typography sx={{ fontSize: 13.5, color: C.muted, mt: 1, lineHeight: 1.6 }}>
            This removes them from this job for good — their resume, their score
            and any interview they sat. It cannot be undone.
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: C.faint, mt: 1.25, lineHeight: 1.55 }}>
            If they have applied to another job, that application is left alone
            and their record is kept.
          </Typography>
          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2.5 }}>
            <Button
              onClick={() => setPendingDelete(null)}
              sx={{ textTransform: "none", fontWeight: 650, color: C.muted }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              disableElevation
              onClick={() => doDelete(pendingDelete)}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                bgcolor: "#dc2626",
                "&:hover": { bgcolor: "#b91c1c" },
              }}
            >
              Delete
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={Boolean(toast)}
        // An undo needs reading time plus deciding time, and a link needs long
        // enough to actually be selected.
        autoHideDuration={toast?.link || toast?.undo ? 14000 : 6000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={toast?.severity || "info"}
          onClose={() => setToast(null)}
          sx={{ maxWidth: 620, fontFamily: FONT, borderRadius: 3, fontSize: 13.5 }}
          action={
            toast?.undo ? (
              // Undo lives in the toast as well as on the row. The row is the
              // durable way back; this is the one that is under the cursor in
              // the two seconds after a misclick.
              <Button
                size="small"
                color="inherit"
                onClick={() => {
                  const run = toast.undo;
                  setToast(null);
                  run();
                }}
                sx={{ fontFamily: FONT, fontWeight: 700, textTransform: "none" }}
              >
                Undo
              </Button>
            ) : toast?.link && !toast?.showLink ? (
              // Not offered when showLink is set: that branch exists BECAUSE the
              // clipboard refused, and a copy button there is the thing that
              // just failed.
              <Tooltip title="Copy the interview link">
                <IconButton
                  size="small"
                  color="inherit"
                  onClick={() => navigator.clipboard?.writeText(toast.link)}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null
          }
        >
          {toast?.message}
          {toast?.showLink && toast?.link && (
            // Selectable, not just copyable. This branch only runs when the
            // clipboard refused, so a second copy button would be the thing
            // that already failed.
            <Box
              component="code"
              sx={{
                display: "block",
                mt: 0.75,
                p: 1,
                borderRadius: 1.5,
                bgcolor: "rgba(0,0,0,.06)",
                fontSize: 11.5,
                wordBreak: "break-all",
                userSelect: "all",
              }}
            >
              {toast.link}
            </Box>
          )}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CandidatePipeline;
